import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { useSubscriptionManagerContext } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.context';
import { shouldReactivateInPlace } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.functions';
import type { tPaymentMethod } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.types';
import { sentry } from 'src/sentry/sentry.singleton';
import {
  clearAttemptId,
  clearThresholdAttemptId,
  closesThresholdAttempt,
  readOrCreateOneOffAttempt,
  readOrCreateThresholdAttemptId,
} from '../../Wallet.attempt';
import {
  computeTopUpPrefillAmount,
  customAmountToMicros,
  formatWalletAmount,
  formatWalletDate,
  isZeroOrNegativeMicros,
  walletAmountAffixes,
} from '../../Wallet.money';
import {
  WalletContractError,
  WalletDeclinedError,
  WalletService,
} from '../../services/Wallet.service';
import { useThresholdTopUpFlag } from '../../hooks/useThresholdTopUpFlag';
import { useTopUpRoleGate } from '../../hooks/useTopUpRoleGate';
import {
  activeThresholdOf,
  buildThresholdForm,
  initialThresholdDraft,
  isKnownBlockedBy,
  pausedThresholdOf,
  thresholdInForce,
  typeBlockedOf,
  type tThresholdDraft,
} from './TopUpModal.threshold';
import {
  chargeErrorMessage,
  recurringErrorMessage,
  recurringThresholdConflict,
  thresholdSaveFailure,
  thresholdSaveGenericMessage,
  type tTopUpChargeErrorMessage,
} from './TopUpModal.chargeError';
import ReplaceRecurringModal from '../ReplaceRecurringModal/ReplaceRecurringModal.component';
import RepeatTopUpModal from '../RepeatTopUpModal/RepeatTopUpModal.component';
import TopUpOptionsErrorModal from '../TopUpOptionsErrorModal/TopUpOptionsErrorModal.component';
import { classifyTopUpOptionsFailure } from './TopUpModal.optionsFailure';
import { useWalletContext } from '../../Wallet.context';
import TopUpModal from './TopUpModal.component';
import TopUpPaymentSection from './TopUpPaymentSection.component';
import type {
  tThresholdAmount,
  tThresholdBlockedBy,
  tThresholdSaveResult,
  tThresholdTopUpState,
  tTopUpOutcome,
  tWalletShortfall,
} from '../../Wallet.types';
import type {
  tTopUpKind,
  tTopUpTypeBlocked,
  tTopUpOptionsFailureKind,
  tTopUpOptionsPayload,
  tTopUpPreset,
} from './TopUpModal.types';

/**
 * Tope de dígitos para tratar `minFreeAmountMicros` como LEGIBLE.
 *
 * Micros son micro-unidades de USD/MXN: 1e6 micros = 1 unidad, así que 15
 * dígitos son del orden de mil millones de unidades — muy por encima de
 * cualquier piso real de negocio y muy por debajo de los ~9e19 micros donde
 * `formatWalletAmount` lanza `RangeError` (roundMicrosToCents rebasa
 * `Number.MAX_SAFE_INTEGER`). Un piso con más dígitos que esto no es un dato
 * de negocio, es un payload corrupto, y el guard de abajo lo trata igual que
 * uno no numérico.
 */
const MAX_READABLE_MIN_FREE_AMOUNT_DIGITS = 15;

/**
 * Owns the modal's state, loads its options and submits the charge (RET-1690).
 *
 * Kept apart from the presentational component so every visual state stays
 * renderable without a backend, and so the state machine — which kind, which
 * preset, what was typed — is testable on its own.
 *
 * THIS IS ALSO THE FORMATTING BOUNDARY. The backend sends exact integers, raw
 * enums and RFC3339 instants; every piece of human text below is built here,
 * from the viewer's locale. That is the [REGLA] closed in the wallet decisions
 * log on 2026-08-05 and the reason Wallet.money.ts exists — see its header.
 */
export default function ConnectedTopUpModal(props: {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Con qué pestaña abre, según por dónde entró el cliente: el switch de recarga
   * automática abre en 'recurring', el botón de recargar en 'one-off'.
   *
   * Es sólo el estado INICIAL. Una vez abierto, la pestaña la manda el cliente
   * desde el propio modal — atarla a la prop lo dejaría sin poder cambiarla.
   */
  initialKind?: tTopUpKind;
  /**
   * El faltante EXACTO a cubrir, en micro-unidades (RET-1806, DS-03: el CTA
   * "Recargar" del compositor sin saldo salta aquí con el monto prellenado).
   * `null`/ausente = comportamiento de siempre, sin nada que prellenar.
   *
   * SOLO el estado INICIAL, igual que `initialKind`: una vez abierto, el
   * monto libre lo manda el cliente desde el propio campo.
   */
  initialShortfall?: tWalletShortfall | null;
  /**
   * Fires with WHAT HAPPENED, once, right before the modal closes (RET-1928).
   *
   * Only a real outcome fires it. A decline does not — it throws before this
   * runs and leaves the modal open. Closing the window without doing anything
   * does not either: that is `onClose`, and the split between the two hooks is
   * the whole distinction between regret and action.
   *
   * The modal informs, it does not decide. A charge travels exactly as the wire
   * returned it; the recurring alta travels with the labels this component
   * already built for the summary, so the confirmation cannot disagree with the
   * sentence the customer just read. What each outcome then triggers lives in
   * `Wallet.outcome.ts`, not here.
   */
  onOutcome?: (outcome: tTopUpOutcome) => void;
}) {
  const {
    isOpen,
    onClose,
    initialKind = 'one-off',
    initialShortfall = null,
    onOutcome,
  } = props;
  /**
   * Si este montaje abrió a PRELLENAR (RET-1806). Congelado en un ref al
   * primer render: `initialShortfall` es solo el estado INICIAL (mismo
   * contrato que `initialKind`, arriba) y este componente se remonta entero
   * cada vez que el hook lo abre (`useTopUpModal`), así que un ref por
   * instancia es exactamente "una vez por apertura", sin necesitar un efecto
   * de reset.
   */
  const prefillActiveRef = useRef(Boolean(initialShortfall));
  const prefillFloorAppliedRef = useRef(false);
  /**
   * Si esta apertura del modal YA sembró una selección inicial (RET-2104).
   *
   * Mismo molde que `prefillFloorAppliedRef`, arriba: un ref que corre UNA
   * SOLA VEZ por apertura, precisamente porque el efecto de carga se repite
   * más de una vez en la misma sesión del modal (depende de
   * `paymentMethods.length` a propósito, ver el efecto). Sin este guardia,
   * cada recarga —el cliente agrega una tarjeta sin cerrar el modal— volvía a
   * sembrar `defaultPresetMicros` encima de lo que el cliente ya había
   * elegido (un preset distinto, o un monto libre tecleado), sin soltar
   * `customAmount`: de ahí las dos cifras conviviendo que reportó el ticket.
   *
   * El guardia va sobre la SIEMBRA, no sobre la RECARGA: `setOptions` y el
   * refetch en sí siguen ocurriendo en cada cambio de `paymentMethods.length`
   * (RET-1923 depende de que ese refresco siga vivo). Solo la escritura de
   * `pickedPresetMicros` se salta a partir de la segunda vez.
   */
  const initialSeedAppliedRef = useRef(false);
  const { currentWorkspace } = useWorkspaceContext();
  /*
    Las tarjetas se leen del manejador de suscripción, que es un provider de
    aplicación (ver TopUpPaymentSection). Antes esta señal bajaba por props desde
    WhatsappApi atravesando dos componentes; leerla aquí la acerca a quien la usa
    y quita esa plomería de las tres pantallas que montan el modal.
  */
  const {
    paymentMethods,
    reactivatePlan,
    cardSavedAsDefaultToken,
    cardSavedWithoutDefaultToken,
    cardSavedWithoutDefaultPaymentMethodId,
    selectedPaymentMethod,
    paymentMethodDefaults,
  } = useSubscriptionManagerContext();
  const { subscription, hasSubscriptionData } = useSubscriptionContext();
  const workspaceId = currentWorkspace?.id ?? '';
  const { t, i18n } = useTranslation('wallet');
  const locale = i18n.language;

  /**
   * PD-4 (leadbilling/docs/business-rules/wallet/pago-vencido.md, 10-ago-2026):
   * la baja de la suscripción está AGENDADA y aún no es efectiva. Es la MISMA
   * señal que ya gobierna el botón Reactivar de SubscriptionOverview — nunca
   * un segundo predicado sobre `status` a secas. Este repo ya documentó el
   * desastre de dos guardias leyendo el mismo hecho de fuentes distintas:
   * docs/solutions/logic-errors/divergent-guard-sources-redirect-loop.md.
   */
  const isScheduledCancellation = shouldReactivateInPlace(subscription);

  /**
   * El aviso/retención de PD-4, con la fecha efectiva YA FORMATEADA — esta es
   * la frontera de formateo del modal, igual que `recurring`/`activeRecurring`
   * abajo. `subscription.endDate` es un `Date` (Subscription.context), no el
   * RFC3339 que `formatWalletDate` espera; el puente vive en
   * `subscriptionEndDateToRfc3339` (helper al final del archivo). Ausente o
   * inválido no se disfraza de fecha: `dateLabel` queda en '' y el componente
   * usa el copy sin fecha en vez de interpolar un hueco — la cancelación ES
   * cierta, pero el día no se inventa.
   *
   * `null` cuando no aplica (suscripción sana, o cancelación ya EFECTIVA): el
   * componente no vuelve a leer `status` para decidir esto, solo la presencia
   * de este objeto.
   */
  const scheduledCancellation = isScheduledCancellation
    ? {
        dateLabel: formatWalletDate(
          subscriptionEndDateToRfc3339(subscription.endDate),
          locale,
        ),
      }
    : null;

  const [options, setOptions] = useState<tTopUpOptionsPayload | null>(null);
  /**
   * La primera carga de opciones de esta apertura falló, y de qué clase
   * (RET-2280).
   *
   * Vive aparte de `options` porque `options === null` también significa
   * "todavía cargando", y mientras carga no se debe pintar ningún error. Solo
   * cubre la PRIMERA carga: si una recarga posterior falla, el modal ya tiene
   * opciones que mostrar y la falla va al pie (ver el `catch` del efecto).
   * Por eso no se limpia al cargar: en cuanto hay opciones, ya no se lee.
   */
  const [optionsLoadFailure, setOptionsLoadFailure] =
    useState<tTopUpOptionsFailureKind | null>(null);
  /**
   * Una recarga de opciones falló con el modal ya abierto (RET-2280): el
   * cliente agregó una tarjeta y la segunda lectura no llegó.
   *
   * Vive aparte de `errorMessage` por dos razones. Los handlers de selección
   * limpian `errorMessage` en cada clic, y este aviso no se arregla con un
   * clic: explica por qué las opciones en pantalla pueden estar viejas. Y una
   * recarga posterior que sí llega lo debe quitar, sin tocar un error de cobro
   * que el cliente todavía no ha leído.
   */
  const [optionsRefreshFailure, setOptionsRefreshFailure] =
    useState<tTopUpOptionsFailureKind | null>(null);
  /**
   * El tipo que el cliente PIDIÓ, que no siempre es el que se puede servir.
   *
   * Se lee siempre a través de `kind`, unas líneas abajo. Nombrarlo distinto es
   * el punto: mientras esto era `kind` a secas, la petición y la realidad eran
   * la misma variable y podían separarse sin que nada lo notara.
   */
  const [requestedKind, setRequestedKind] = useState<tTopUpKind>(initialKind);
  /**
   * El preset que se ELIGIÓ, que no siempre pertenece a la escalera que se está
   * pintando. Se lee siempre a través de `selectedPresetMicros`, abajo — mismo
   * par que `requestedKind`/`kind`.
   */
  const [pickedPresetMicros, setPickedPresetMicros] = useState<string | null>(
    null,
  );
  /**
   * ARRANCA VACÍO INCLUSO EN MODO PRELLENADO, y el prellenado entero vive en el
   * efecto que corre cuando llegan las opciones.
   *
   * Antes aquí se adelantaba el techo entero del faltante, con el argumento de
   * que el piso de `minFreeAmountMicros` todavía no se conocía y se aplicaría
   * después. Pero la MONEDA del payload tampoco se conoce en este punto, y sin
   * ella no se puede saber si el faltante y el piso son comparables (ver el
   * efecto). Adelantar la cifra significaba pintar un número derivado de otra
   * moneda y borrarlo un tick más tarde — un parpadeo con dinero equivocado.
   *
   * No se pierde nada: hasta que las opciones aterricen no hay escalera ni
   * total que acompañen a ese número, así que el campo vacío es el estado
   * honesto de "todavía no sé".
   */
  const [customAmount, setCustomAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  /**
   * El mensaje del pie, con SU CLASE pegada (RET-1933).
   *
   * UN estado, no dos. `blocksContinue` no vive en un `useState` paralelo:
   * dos estados que tienen que cambiar siempre juntos son dos estados que un
   * día un `setErrorMessage` sin su `setBlocksContinue` deja desincronizados.
   * Aquí el texto y la clase nacen del mismo lugar en cada asignación —
   * `chargeErrorMessage` para un intento de cobro fallido, un literal
   * `{ blocksContinue: true }` para la validación local de monto— así que no
   * hay forma de mover uno sin el otro.
   *
   * `null` sigue significando "no hay error", exactamente igual que antes.
   */
  const [errorMessage, setErrorMessage] =
    useState<tTopUpChargeErrorMessage | null>(null);
  /**
   * Se guardó una tarjeta CON ESTE MODAL ABIERTO y la recarga sigue sin
   * cobrarse (RET-2348). Pinta «Tarjeta guardada. Tu recarga todavía no se
   * cobra.»
   *
   * EL CASO. Tras un rechazo, un cliente hizo lo que el modal le pidió: agregó
   * otro método de pago. Vio un aviso verde de éxito y lo tomó por el pago. No
   * se había cobrado nada: guardar una tarjeta sólo la guarda, y la recarga
   * seguía esperando un clic en un botón que decía «Continuar». Guardó tres
   * tarjetas y estuvo 1 h 52 min sin saldo.
   *
   * LO QUE NO HACE, y es la decisión que más pesa: NO cobra. El dinero se
   * mueve con un clic explícito en el botón principal y con nada más. Lanzar
   * el cobro al guardar la tarjeta habría «arreglado» este caso cobrándole a
   * quien sólo quería cambiar de tarjeta, o a quien entre tanto cambió el
   * monto.
   *
   * Se enciende en el efecto de más abajo, cuando el manejador de suscripción
   * avisa, y se apaga cuando el cliente RESPONDE a la línea: cobra, cambia de
   * pestaña o cambia el monto. Cerrar el modal lo desmonta. No se apaga solo:
   * un aviso que caduca es justo lo que ya falló.
   */
  const [cardSaved, setCardSaved] = useState(false);
  /**
   * El último valor del token que este modal ya atendió. Arranca en el que
   * hay AL ABRIR, y por eso una tarjeta guardada antes de abrir no cuenta:
   * este componente se remonta entero en cada apertura (`useTopUpModal`), así
   * que un ref por instancia es «una vez por apertura», igual que
   * `prefillActiveRef`.
   */
  const handledCardSavedTokenRef = useRef(cardSavedAsDefaultToken);
  /**
   * Lo mismo para la tarjeta guardada SIN quedar como predeterminada
   * (RET-2428): una prueba que el cliente todavía no confirma. Otra señal y
   * otro ref, porque `cardSavedAsDefaultToken` sigue significando «guardada y
   * predeterminada».
   */
  const handledCardSavedWithoutDefaultTokenRef = useRef(
    cardSavedWithoutDefaultToken,
  );
  /**
   * La tarjeta que la recarga única cobra en vez de la que elige el backend
   * (RET-2428, RC-16): la que se guardó sin quedar como predeterminada, y
   * sólo mientras es la que el modal muestra. Se deriva del manejador de
   * suscripción, que sobrevive al desmontaje, y no de un estado del modal: al
   * reabrir, la tarjeta que se ve es la que se cobra. Fuera de ese caso es
   * `null` y el cobro no manda tarjeta: el backend cobra la predeterminada.
   * Ver `resolveIndicatedPaymentMethodId`, al final del archivo.
   */
  const indicatedPaymentMethodId = resolveIndicatedPaymentMethodId({
    savedWithoutDefaultId: cardSavedWithoutDefaultPaymentMethodId,
    shownId: selectedPaymentMethod?.paymentMethodId,
    customerDefaultId: paymentMethodDefaults?.customerDefaultPaymentMethodId,
    paymentMethods,
  });
  /**
   * La tarjeta indicada que este modal ya atendió. Arranca en la que hay AL
   * ABRIR: al reabrir no hay rechazo en pantalla que quitar.
   */
  const handledIndicatedPaymentMethodIdRef = useRef(indicatedPaymentMethodId);
  /**
   * Confirmación de REEMPLAZO (RET-1692, D-6). Solo aparece al dar de alta una
   * recurrente teniendo otra activa: el aviso inline ya explica qué va a pasar,
   * y esto es el "sí, entendí". Sin una activa no hay nada que reemplazar y
   * pedir confirmación sería una pared de más.
   */
  const [askingToReplace, setAskingToReplace] = useState(false);
  /**
   * El candado de la segunda recarga del mismo monto (RET-1925, criterio 5).
   *
   * LOS DATOS DE LA DECISIÓN, no un booleano, y las dos razones son defectos que
   * la revisión midió sobre la versión booleana.
   *
   * La etiqueta se CONGELA al abrir. Colgada de `chargeInFlight`, se re-derivaba
   * en cada render, y el provider vuelve a `idle` en cuanto el saldo sube — que
   * es el caso COMÚN, porque la ventana entera dura segundos. La frase quedaba
   * en "Tu recarga de  ya se cobró", con un hueco donde va la cifra, pidiéndole
   * al cliente que apruebe un cobro nuevo.
   *
   * Y `null` es la única forma de estar cerrado, así que cerrarlo y desarmar el
   * candado son el mismo acto. Con el booleano había un `false` sólo en el
   * cancelar: tras un rechazo del cobro confirmado el diálogo se quedaba ENCIMA
   * del error, y como un 402 corre `clearAttemptId`, cada clic siguiente acuñaba
   * llave nueva y lanzaba un cobro REAL sin volver a pedir confirmación.
   *
   * El precedente sigue siendo `askingToReplace`, y ahora también en los resets:
   * el comentario de su `catch` dice, textual, que dejar la confirmación abierta
   * tapa el error y "eso invita a volver a pulsar". Aquí eso no invita a repetir
   * un alta: invita a repetir un COBRO.
   */
  const [repeatConfirm, setRepeatConfirm] = useState<{ label: string } | null>(
    null,
  );
  /**
   * El estado de la acreditación, del CONTEXTO.
   *
   * Es la única dependencia nueva del modal hacia el provider, y es inevitable:
   * el candado compara contra el monto que arrancó la espera, y eso vive ahí.
   * Un mount por encima del provider recibe el default `idle` y simplemente no
   * tiene candado — un defecto visible, no una pantalla en blanco, igual que el
   * criterio de los demás defaults del contexto.
   *
   * RET-2348 lee además el SALDO que el provider ya tiene, para el aviso de
   * `walletCannotSend`. No dispara ninguna lectura: sólo mira lo que hay.
   */
  const { settlement, balance, refetchBalance } = useWalletContext();

  /**
   * RET-2425 (KTD2, KTD3): the design that ships with the threshold top-up.
   * The flag of THIS workspace, already loaded, and a role that may top up
   * (R19). With it false nothing below calls `GET …/topup/threshold` and the
   * modal is today's, byte for byte: the existing suites mock `WalletService`
   * with a fixed method list and never see the new call.
   */
  const { canOfferThresholdTopUp, hasLoadedThresholdTopUpFlag } =
    useThresholdTopUpFlag();
  const { canTopUp } = useTopUpRoleGate();
  const thresholdDesign = canOfferThresholdTopUp && canTopUp;

  /**
   * The threshold top-up's state, read on every open (KTD4). `ready` is the
   * only status that offers the option (KTD5): a wallet 404 (no plan, no
   * payment customer), a route missing, a contract error, a 5xx, a 424 or a
   * dropped connection all land in `unavailable`. Reporting is NOT done here:
   * the service reports what is a failure (route missing, contract) and
   * AxiosService reports the rest; a wallet 404 is a business answer and is
   * not reported at all. Reporting again here would double every entry.
   */
  const [thresholdLoad, setThresholdLoad] = useState<
    | { status: 'idle' | 'loading' | 'unavailable' }
    | { status: 'ready'; state: tThresholdTopUpState }
  >({ status: 'idle' });
  /**
   * What the customer typed in the threshold form. `null` until the first
   * edit: the opening values are DERIVED from the GET state (R2), so they are
   * right the moment the state lands, without an effect that seeds them.
   */
  const [thresholdDraftEdit, setThresholdDraftEdit] =
    useState<tThresholdDraft | null>(null);
  /**
   * RET-2425 (RC-22): the confirmation of the threshold top-up replacing the
   * recurring one (or the converted credit package). Its own flag, apart from
   * `askingToReplace`: that one confirms the recurring POST and this one the
   * threshold PUT, and each confirm button calls its own write.
   */
  const [askingThresholdReplace, setAskingThresholdReplace] = useState(false);
  /**
   * RET-2425: a 400 `invalid_amount` of the PUT, painted in the field its
   * `code` belongs to, with the sentence the client check uses. Cleared on any
   * edit: it describes the values that were sent.
   */
  const [thresholdFieldError, setThresholdFieldError] = useState<{
    field: 'threshold' | 'amount';
    text: string;
  } | null>(null);
  /**
   * RET-2425: what a 409 of a write said and the GET did not (the Risks table:
   * the GET reads Stripe live and, when that read fails, answers
   * `typeChange.allowed: true`). The server's answer wins over that read for
   * the rest of this open.
   *
   * - `serverTypeBlock`: 409 `past_due` / `credit_package_scheduled` (RC-22).
   * - `serverCancellation`: 409 `cancellation_scheduled`, wallet's backstop for
   *   PD-4 when this modal had no subscription data.
   */
  const [serverTypeBlock, setServerTypeBlock] = useState<{
    kind: 'threshold' | 'recurring';
    reason: tThresholdBlockedBy;
  } | null>(null);
  const [serverCancellation, setServerCancellation] = useState(false);
  /**
   * RET-2425: a threshold PUT is in flight. A ref and not only `isSubmitting`:
   * two clicks in the same tick both read the `isSubmitting` of the render
   * they were bound to, so the state cannot stop the second one. The PUT can
   * charge a card.
   */
  const thresholdSaveInFlightRef = useRef(false);
  /**
   * RET-2425: THIS instance has shown the threshold option as the chosen kind.
   * It separates two requests that derive the same `kind` (see `submit()`):
   * a threshold request that was never servable falls back to the form the
   * customer saw from the first paint, and saving that form is legit; one that
   * WAS shown and then lost the option (flag or role off) is stale, and its
   * click must not post the recurring or one-off form in its place.
   */
  const thresholdShownRef = useRef(false);
  /**
   * RET-2425: whether THIS instance is still mounted. The threshold PUT can
   * outlive the modal: closed and reopened while it travels, `useTopUpModal`
   * mounts a NEW instance, and the old continuation's `onClose` would close
   * that new modal. After any await, `onClose` runs only while mounted;
   * `onOutcome` still runs, so the balance settlement starts.
   */
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Read through a ref so the load effect does not re-run when the provider
  // hands out a new function identity.
  const refetchBalanceRef = useRef(refetchBalance);
  refetchBalanceRef.current = refetchBalance;

  /**
   * El wallet no puede enviar HOY (RET-2348). Dos señales, las dos ya en
   * memoria; ninguna pide una lectura nueva:
   *
   * - El modal ABRIÓ PARA CUBRIR UN FALTANTE (`prefillActiveRef`): es la barra
   *   sin saldo del compositor, que sólo existe cuando un envío concreto no
   *   alcanza. Es la señal que cubre «el saldo no llega ni para un mensaje»,
   *   porque el costo de un mensaje no lo conoce este modal: lo conoce quien
   *   cotizó, y el faltante es su respuesta.
   * - El SALDO que el provider tiene está en cero o por debajo. Cubre las
   *   otras tres puertas (pestaña de Saldo, WhatsApp API, Ajustes), que abren
   *   sin faltante. `balance` sólo es no-nulo con el saldo LEÍDO del workspace
   *   en pantalla (`status: 'ready'`): cargando, con la lectura caída o arriba
   *   del provider es `null`, y `isZeroOrNegativeMicros` contesta `false` ante
   *   lo que no puede leer. «No sé» nunca se pinta como «sin saldo».
   *
   * Sólo decide un AVISO en la pestaña automática. No bloquea nada ni decide
   * dinero: si la señal falta, lo peor que pasa es la pantalla de antes.
   */
  const walletCannotSend =
    prefillActiveRef.current || isZeroOrNegativeMicros(balance?.amountMicros);

  /**
   * Si la recarga automática se puede ofrecer, y por lo tanto si se puede
   * ELEGIR. Dos condiciones, las dos necesarias:
   *
   * - Que el backend mande su oferta. `recurring: null` es parte del contrato
   *   (`tWireRecurring | null`), y sin ella no hay cadencia, ni fecha, ni
   *   escalera contra la que validar el alta.
   * - Que SEPAMOS el estado de la suscripción (RET-1908). Los valores por
   *   defecto de `subscription` —`INACTIVE`, `pendingCancellation: false`— son
   *   inventados y se leen igual que una respuesta real, así que sin esto PD-4
   *   falla ABIERTO: a un cliente con la baja agendada se le ofrece configurar
   *   una recarga automática que nunca se va a cobrar, sin aviso y sin momento
   *   de retención.
   *
   * `=== true` y no un `Boolean(...)`: el campo viene de un contexto que 80
   * consumidores comparten, y un `undefined` —un provider ausente, un doble
   * parcial— tiene que leerse como «no sé», que aquí significa «no la
   * ofrezcas». El default del componente hace lo mismo por su lado.
   */
  const canChooseRecurring =
    Boolean(options?.recurring) && hasSubscriptionData === true;

  /**
   * EL TIPO QUE DE VERDAD RIGE, derivado — no un estado que alguien reconcilie.
   *
   * `requestedKind` puede pedir 'recurring' sin que se pueda servir: el modal se
   * abre así desde el switch de la tarjeta de saldo (`openRecurring`, tres
   * puntos de montaje) y ese switch no sabe nada del payload. Mientras esto era
   * un estado suelto, la pestaña dejaba de ofrecerse pero `requestedKind` se
   * quedaba en 'recurring', y con él: sin campo de monto libre y con Continuar
   * contestando «escribe un monto válido» sobre una pantalla donde no hay nada
   * que escribir. Peor con oferta presente y estado desconocido — el alta se
   * completaba igual y el gate de arriba quedaba decorativo.
   *
   * DERIVADO Y NO UN `useEffect` que resetee: mientras las opciones viajan,
   * `options` es null y `canChooseRecurring` es false. Un efecto dispararía en
   * esa ventana y destruiría la intención del switch para TODO cliente, no solo
   * para los que no tienen oferta. Derivado, la intención sobrevive al vuelo y
   * solo se descarta si al aterrizar el payload no hay nada que ofrecer.
   */
  /*
   * RET-2425: the threshold state, once it is usable. The option is offered
   * only with a 200 (KTD5) whose currency is the options' one: the presets
   * the form uses are the one-off ones (RC-09), and a limit in one currency
   * against presets in another would compare two numbers, not two amounts.
   */
  const thresholdState =
    thresholdDesign &&
    thresholdLoad.status === 'ready' &&
    options !== null &&
    thresholdLoad.state.currency === options.currency
      ? thresholdLoad.state
      : null;
  const canChooseThreshold = thresholdState !== null;
  /** The GET has not settled yet, so the option's spot holds a placeholder. */
  const thresholdPending =
    thresholdDesign &&
    (thresholdLoad.status === 'idle' || thresholdLoad.status === 'loading');

  /*
   * RET-2425: only `requestedKind === 'threshold'` is new. It gives the
   * threshold top-up when it is available; else the recurring one, if that one
   * can be chosen; else the one-off. Every other request follows today's rule,
   * unchanged. Still derived, for the reason above: while the GET travels the
   * request survives, and the modal waits in its loading state (below) instead
   * of throwing the request away.
   */
  const kind: tTopUpKind =
    requestedKind === 'threshold'
      ? canChooseThreshold
        ? 'threshold'
        : canChooseRecurring
          ? 'recurring'
          : 'one-off'
      : canChooseRecurring
        ? requestedKind
        : 'one-off';
  // Written in render, like `refetchBalanceRef`: it records a fact about what
  // this render shows, it never triggers one, and it only ever goes to true.
  if (kind === 'threshold') thresholdShownRef.current = true;

  /**
   * EL PRESET QUE DE VERDAD RIGE: la selección reconciliada contra la escalera
   * que se está pintando.
   *
   * Cada tipo de recarga tiene su propia escalera y en un no-mensual no se
   * cruzan (RC-03, ×periodicidad), así que un monto elegido bajo una es
   * fantasma bajo la otra: ningún botón queda marcado, el total del pie cae a
   * vacío y `canContinue` sigue VIVO —solo mira que haya algo seleccionado, no
   * que ese algo exista en la lista de enfrente—, así que Continuar responde
   * «escribe un monto válido» sobre una pestaña sin campo donde escribir.
   *
   * POR QUÉ RECONCILIAR Y NO AÑADIR OTRA ESCRITURA. La selección se escribía en
   * dos puntos —la carga de opciones y el clic del cliente— mientras `kind` ya
   * gira desde TRES: la carga, el clic en la pestaña, y la llegada del dato de
   * suscripción (giro que introdujo el derivado de arriba). Parchar el punto que
   * falta deja el siguiente suelto; reconciliar aquí cubre los tres y cualquiera
   * que se agregue después.
   *
   * LA REGLA ES ESTRECHA A PROPÓSITO: solo sustituye un monto NO-MIEMBRO.
   *
   * - Ausente sigue ausente. `null` no es una selección inválida, es la
   *   ausencia de selección: es el monto libre (exclusivo de la única, que anula
   *   la escalera) y es también el «ausente = sin sugerencia» del contrato
   *   durante el rollout de `defaultPresetMicros`. Rellenar ese hueco marcaría
   *   un preset mientras el cliente teclea su propia cifra.
   * - Una elección válida del cliente nunca se destruye. Si el monto pertenece
   *   a la escalera pintada se respeta tal cual: una regla más ancha —«al girar,
   *   siempre el sugerido de la pestaña»— convertiría en $50 un monto que el
   *   cliente eligió a mano, sobre una superficie de dinero y sin avisarle.
   *
   * Las ramas de `onKindChange` se quedan y no son redundantes: cubren el caso
   * que esta regla deja fuera por diseño — venir del monto libre, donde la
   * selección es `null` y D-3 pide caer al sugerido de la pestaña destino.
   */
  const selectedPresetMicros = useMemo(() => {
    if (!pickedPresetMicros || !options) return pickedPresetMicros;

    // Las dos escaleras exponen la misma forma (`presets` + `defaultPresetMicros`),
    // así que se eligen como objeto y la regla se escribe una sola vez.
    const ladder =
      kind === 'recurring' && options.recurring ? options.recurring : options;

    if (
      ladder.presets.some(
        (preset) => preset.amountMicros === pickedPresetMicros,
      )
    ) {
      return pickedPresetMicros;
    }

    return ladder.defaultPresetMicros || null;
  }, [pickedPresetMicros, options, kind]);

  /*
  El `attemptId` ya NO vive aquí (RET-1860). Vive en sessionStorage, por
  workspace, y este componente solo lo lee y lo cierra.

  Antes era un `useRef` que se regeneraba al abrir el modal. Eso tenía un hueco:
  un reload entre el cobro en Stripe y la respuesta HTTP creaba una intención
  nueva, y con ella una llave nueva — o sea, el doble cobro que RET-1852 cerró,
  reabierto justo en el escenario donde la red falla, que es cuando la gente
  recarga la página.

  Y por eso desapareció la regeneración al abrir: si el modal fabricara una
  intención nueva cada vez que se abre, reabrirlo tras el reload volvería a
  perder la llave. Lo que define el fin de una intención NO es cerrar el modal,
  es recibir una respuesta DEFINITIVA. Eso lo decide `clearAttemptId`, abajo.
  */

  /**
   * RET-2425: the threshold state, read on open, in parallel with the options
   * (KTD4). And the balance, re-read on open (KTD8): «your balance is already
   * below the limit» is a prediction from it, and a balance cached from an
   * hour ago would promise a charge that does not happen, or hide one.
   */
  useEffect(() => {
    if (!isOpen || !workspaceId || !thresholdDesign) return;

    let cancelled = false;

    setThresholdLoad({ status: 'loading' });
    // A failed re-read leaves the balance the provider has; the prediction is
    // only a notice, and wallet decides the charge with its own balance.
    void refetchBalanceRef.current().catch(() => null);

    async function loadThreshold() {
      try {
        const state = await WalletService.getThresholdTopUp(workspaceId);
        if (cancelled) return;
        setThresholdLoad({ status: 'ready', state });

        const blockedBy = state.typeChange.blockedBy;
        if (blockedBy !== null && !isKnownBlockedBy(blockedBy)) {
          // Treated as allowed (the PUT and the POST check again and answer
          // 409), and reported once per open: a reason this front cannot name
          // is a contract this front does not know yet.
          sentry.captureException(
            new WalletContractError(
              `wallet threshold top-up: unknown typeChange.blockedBy "${blockedBy}"`,
            ),
            { extra: { workspaceId } },
          );
        }
      } catch {
        if (cancelled) return;
        setThresholdLoad({ status: 'unavailable' });
      }
    }

    void loadThreshold();

    return () => {
      cancelled = true;
    };
  }, [isOpen, workspaceId, thresholdDesign]);

  useEffect(() => {
    if (!isOpen || !workspaceId) return;

    let cancelled = false;

    // Inner async function because the effect callback must return the cleanup
    // function, not a promise.
    async function loadOptions() {
      try {
        const data = await WalletService.getTopUpOptions(workspaceId);
        if (cancelled) return;
        setOptions(data);
        setOptionsRefreshFailure(null);
        // Which preset starts selected is a backend decision (D-1), and it
        // arrives as the AMOUNT, not as a position: a position would select a
        // different figure the day the list is filtered or reordered.
        //
        // EXCEPTO en modo prellenado (RET-1806): el monto libre YA está
        // eligiendo por el cliente (cubrir el envío), y marcar un preset
        // encima haría que el TOTAL del pie cayera al preset sugerido en vez
        // del monto que el CTA prometió cubrir — justo el hueco que
        // `Template.walletGate.topUpPrefill.test.tsx` (F) exige cerrar.
        //
        // Y EXCEPTO a partir de la SEGUNDA vez que este efecto corre en la
        // misma apertura (RET-2104, `initialSeedAppliedRef`): esta llamada es
        // el único punto de mutación de la selección que dispara sin que el
        // cliente haga nada, así que una recarga de opciones más tarde en la
        // misma sesión del modal no puede pisar un preset o un monto libre
        // que el cliente ya haya elegido a mano.
        if (!prefillActiveRef.current && !initialSeedAppliedRef.current) {
          setPickedPresetMicros(data.defaultPresetMicros || null);
        }
        initialSeedAppliedRef.current = true;
      } catch (error) {
        if (cancelled) return;
        const failure = classifyTopUpOptionsFailure(error);

        // Ya hubo una carga buena en esta apertura (`initialSeedAppliedRef`
        // solo se enciende tras un éxito): el cliente agregó una tarjeta y la
        // recarga falló. Se conservan las opciones y la falla va al pie. Sin
        // el aviso, «Continuar» podría seguir apagado por un
        // `hasPaymentMethod` viejo y nadie diría por qué. Antes, este camino
        // hacía `setOptions(null)` y el modal abierto desaparecía.
        if (initialSeedAppliedRef.current) {
          setOptionsRefreshFailure(failure);
          return;
        }

        setOptionsLoadFailure(failure);
      }
    }

    void loadOptions();

    return () => {
      cancelled = true;
    };
    // El número de tarjetas entra como dependencia para releer las opciones en
    // cuanto el cliente agrega una sin cerrar el modal. El backend es quien dice
    // si hay método de pago, así que la respuesta se le vuelve a pedir a él en
    // vez de deducirla aquí.
  }, [isOpen, workspaceId, paymentMethods.length]);

  /**
   * Aplica el PISO de RC-10 al prellenado, en cuanto se conoce
   * `minFreeAmountMicros` (RET-1806, DS-03 + DS-04).
   *
   * Corre UNA SOLA VEZ por apertura (`prefillFloorAppliedRef`): sin ese
   * guardia, una recarga de opciones más tarde en la misma sesión del modal
   * (el efecto de arriba también depende de `paymentMethods.length`) pisaría
   * cualquier ajuste que el cliente haya hecho a mano sobre el campo, mucho
   * después de que el prellenado ya cumplió su propósito.
   */
  useEffect(() => {
    if (!prefillActiveRef.current) return;
    if (prefillFloorAppliedRef.current) return;
    if (!options || !initialShortfall) return;

    prefillFloorAppliedRef.current = true;

    // La regla de qué se prellena —y cuándo NO— vive en
    // `resolveShortfallPrefill`, al final del archivo, porque desde RET-2348
    // la aplica también el regreso a la recarga única. Sin nada comparable se
    // deja el campo vacío con la escalera a la vista.
    const prefill = resolveShortfallPrefill(initialShortfall, options);

    if (prefill) setCustomAmount(prefill);
  }, [options, initialShortfall]);

  /**
   * Una tarjeta se guardó con el modal abierto (RET-2348).
   *
   * `cardSavedAsDefaultToken` sube en el manejador de suscripción justo donde
   * sale el aviso «Tarjeta guardada», y SÓLO cuando la tarjeta además quedó
   * como predeterminada. Es la condición que importa aquí: el cobro del wallet
   * no manda tarjeta, la elige el backend y es la predeterminada. Si la
   * promoción falla, la que se va a cobrar sigue siendo la de antes, así que
   * ni la línea ni el borrado de abajo serían verdad — ese camino ya tiene su
   * propia advertencia y aquí no se toca nada.
   *
   * Un token como dependencia y no una función, por lo mismo que
   * `autoTopUpToken`: el alta de tarjeta vive en un provider de aplicación y
   * este modal se monta en varias superficies. Con un token, quien guarda no
   * necesita saber quién escucha.
   *
   * Hace DOS cosas, y las dos en el mismo instante:
   *
   * - Enciende la línea.
   * - Quita el rechazo del banco, si era lo que estaba en pantalla
   *   (`clearsOnNewCard`). Ese texto hablaba de la tarjeta anterior; dejarlo
   *   junto a la tarjeta nueva ya elegida le dice al cliente que su cobro
   *   falló con una tarjeta con la que nadie ha intentado cobrar. Se BORRA del
   *   estado en vez de sólo esconderse, para que no vuelva cuando la línea se
   *   apague. Cualquier otro error se queda: no hablaba de la tarjeta.
   *
   * Una promoción que ocurra con el modal cerrado no pasa por aquí: el ref
   * arranca en el valor del token al montar.
   */
  useEffect(() => {
    if (cardSavedAsDefaultToken === handledCardSavedTokenRef.current) return;

    handledCardSavedTokenRef.current = cardSavedAsDefaultToken;
    setCardSaved(true);
    setErrorMessage((current) => (current?.clearsOnNewCard ? null : current));
  }, [cardSavedAsDefaultToken]);

  /**
   * La tarjeta se guardó SIN quedar como predeterminada (RET-2428, R9): la
   * prueba todavía no está confirmada. La pantalla dice lo mismo que fuera de
   * la prueba, porque lo que importa aquí es igual de cierto: la tarjeta se
   * guardó y la recarga todavía no se cobra. El aviso flotante del manejador
   * de suscripción ya dice que la tarjeta no quedó como predeterminada.
   *
   * NO quita el rechazo del banco: en este instante el modal todavía muestra,
   * y cobraría, la tarjeta de antes. Lo quita el efecto de abajo cuando la
   * tarjeta nueva pasa a ser la que se muestra y se cobra (RC-16).
   */
  useEffect(() => {
    if (
      cardSavedWithoutDefaultToken ===
      handledCardSavedWithoutDefaultTokenRef.current
    ) {
      return;
    }

    handledCardSavedWithoutDefaultTokenRef.current =
      cardSavedWithoutDefaultToken;
    setCardSaved(true);
  }, [cardSavedWithoutDefaultToken]);

  /**
   * La tarjeta indicada cambió a otra con el modal abierto (RET-2428, RC-16):
   * el reintento ya no va a la tarjeta que el banco rechazó, así que el
   * rechazo se quita igual que al guardar una predeterminada.
   */
  useEffect(() => {
    if (
      indicatedPaymentMethodId === handledIndicatedPaymentMethodIdRef.current
    ) {
      return;
    }

    handledIndicatedPaymentMethodIdRef.current = indicatedPaymentMethodId;

    if (!indicatedPaymentMethodId) return;

    setErrorMessage((current) => (current?.clearsOnNewCard ? null : current));
  }, [indicatedPaymentMethodId]);

  /** Every preset's label, built here from its exact integer. */
  const presets = useMemo<tTopUpPreset[]>(() => {
    if (!options) return [];

    /*
      CADA TIPO DE RECARGA TIENE SU PROPIA ESCALERA, y confundirlas no era un
      detalle cosmético: hacía IMPOSIBLE dar de alta la recarga automática a
      todo cliente que no sea mensual.

      El BFF sirve las dos listas desde dos endpoints distintos, porque RC-03
      dice que los presets de la automática escalan ×periodicidad y los de la
      única NO escalan. Para un trimestral: única 25/50/100/200, automática
      75/150/300/600.

      La escalera pintada era SIEMPRE la de la única, mientras que el alta
      valida el monto elegido contra `options.recurring.presets`. Para ese
      trimestral, ninguno de los montos que la pantalla le ofrecía existía en
      la lista contra la que se valida: elegía uno, pulsaba Continuar, y el
      modal le respondía que escribiera un monto válido — culpando al monto que
      la propia pantalla acababa de ofrecerle.
    */
    const source =
      kind === 'recurring' && options.recurring
        ? options.recurring.presets
        : options.presets;

    return source.map((preset) => ({
      amountMicros: preset.amountMicros,
      // Sin los ".00": los presets son montos cerrados y el decimal es ruido que
      // el cliente lee en cada opción. `trimZeroCents` sólo los suelta cuando de
      // verdad no hay centavos, así que un preset que algún día los traiga los
      // sigue mostrando. El total hereda esta etiqueta cuando hay preset elegido;
      // el monto libre no pasa por aquí, y ahí los centavos son del cliente.
      label: formatWalletAmount({
        amountMicros: preset.amountMicros,
        currency: options.currency,
        locale,
        trimZeroCents: true,
      }),
    }));
  }, [options, kind, locale]);

  const recurring = useMemo(() => {
    if (!options?.recurring) return null;

    /*
      `periodicity` es un enum ABIERTO del backend, no un campo garantizado.
      Sin él no hay palabra de cadencia que mostrar — ni adjetivo ni adverbio
      — y `t(..., { defaultValue: periodicity })` con `periodicity` ausente
      devuelve la LLAVE cruda (i18next: un `defaultValue: undefined` no cuenta
      como default) y con `periodicity: ''` devuelve la cadena vacía. Los dos
      casos aterrizaban en el pie y en `start.explain` como "cadence=undefined"
      o una preposición colgando ("mensualmente" ausente, " desde el ..."),
      exactamente la clase de dato malformado que este modal ya sufrió dos
      veces (RC-10, PD-4).

      Tratarlo como AUSENTE — `recurring` entero en `null` — enruta al mismo
      branch que ya existe para "el backend no mandó info de recurrente"
      (el hint viejo en el pie, y el bloque de cadencia/fecha del cuerpo
      oculto): una oferta recurrente sin cadencia no es una oferta coherente,
      así que no hay nada bueno que mostrar en su lugar.
    */
    /*
      Sin cadencia NO se tira la fecha, que es buena por su cuenta: RC-04 dice
      que «la fecha del próximo ciclo se muestra informativa». Antes este
      guardia devolvía `null` y se perdían las dos cosas.

      Lo que sí se calla es el RESUMEN del pie: una frase que promete un cobro
      recurrente sin decir cada cuánto entiende de menos un compromiso, y eso
      es peor que no resumir. El pie cae a su subtítulo de siempre y el cuerpo
      conserva la fila de la fecha. Quien decide es `cadenceAdverbLabel: null`.
    */
    const periodicity = options.recurring.periodicity;

    return {
      // An unrecognised cadence falls back to the raw enum instead of blanking
      // the line: the set is open on the backend, and showing "quarterly" is
      // worse copy but better than a label that silently disappears.
      cadenceLabel: periodicity
        ? t(`topUp.cadence.values.${periodicity}`, {
            defaultValue: periodicity,
          })
        : null,
      // RC-04: the adverbial form, same open-enum fallback as `cadenceLabel`
      // above — an unrecognised cadence has no adverb key either, so it falls
      // back to the same raw enum rather than an empty phrase.
      cadenceAdverbLabel: periodicity
        ? t(`topUp.cadence.adverb.${periodicity}`, {
            defaultValue: periodicity,
          })
        : null,
      startDateLabel: formatWalletDate(options.recurring.firstCharge, locale),
    };
  }, [options, locale, t]);

  const activeRecurring = useMemo(() => {
    const active = options?.activeRecurring;

    // Se exige el MONTO además del flag: sin él no se puede escribir "reemplaza
    // la de X", y una confirmación con el hueco vacío es peor que no pedirla.
    if (!options || !active?.active || !active.amount) return null;

    return {
      // Mismo trato que los presets: este monto SALE de uno, y se lee junto a
      // ellos en la misma pantalla. Formatearlo distinto haría ver "$1,000.00"
      // debajo de un botón que dice "$1,000".
      amountLabel: formatWalletAmount({
        amountMicros: active.amount.amountMicros,
        currency: options.currency,
        locale,
        trimZeroCents: true,
      }),
      startDateLabel: formatWalletDate(active.nextCharge, locale),
      // RET-2259: se compara contra el literal y no se castea. Ausente o
      // desconocido cae a `wallet`, que es el comportamiento de antes del campo.
      source:
        active.source === 'credit_package'
          ? ('credit_package' as const)
          : ('wallet' as const),
    };
  }, [options, locale]);

  /** RET-2425: the threshold top-up in force, for the replace notices. */
  const activeThreshold = useMemo(
    () =>
      thresholdState
        ? activeThresholdOf({ state: thresholdState, locale })
        : null,
    [thresholdState, locale],
  );

  /**
   * RET-2425 (RC-22): the automatic type that cannot be chosen now. A reason
   * this front does not know is treated as allowed; the load effect reports it.
   */
  const typeBlocked = useMemo(
    () =>
      thresholdState
        ? typeBlockedOf({
            state: thresholdState,
            hasActiveRecurring: activeRecurring !== null,
            locale,
            formatDate: formatWalletDate,
          })
        : null,
    [thresholdState, activeRecurring, locale],
  );

  /**
   * RET-2425: the threshold form, validated and labelled by the pure module
   * (`TopUpModal.threshold.ts`). The presets are the one-off ones (RC-09):
   * `presets` above already serves `options.presets` for every kind but
   * `recurring`.
   */
  const thresholdForm = useMemo(() => {
    if (!thresholdState || kind !== 'threshold') return null;

    const draft =
      thresholdDraftEdit ??
      initialThresholdDraft({ state: thresholdState, presets });

    return {
      draft,
      ...buildThresholdForm({
        state: thresholdState,
        presets,
        draft,
        balance,
        locale,
        t,
      }),
    };
  }, [thresholdState, kind, thresholdDraftEdit, presets, balance, locale, t]);

  const totalLabel = useMemo(() => {
    if (!options) return '';

    const preset = presets.find(
      (candidate) => candidate.amountMicros === selectedPresetMicros,
    );

    if (preset) return preset.label;

    // The customer's own input. It is NOT run through formatWalletAmount: that
    // one takes exact micro-units, and what is in the field is a partial string
    // being typed. Reformatting it on every keystroke would fight the caret.
    // Los afijos se derivan del MISMO formateador que los presets, así que la
    // cifra escrita a mano se lee igual que las de arriba — incluido el código
    // de la moneda, que ahora va al final.
    if (!customAmount) return '';

    const { prefix, suffix } = walletAmountAffixes({
      currency: options.currency,
      locale,
    });

    return `${prefix}${customAmount}${suffix}`;
  }, [options, presets, selectedPresetMicros, customAmount, locale]);

  /**
   * RC-10's floor on the free amount, read from the payload (RET-1895).
   *
   * `options?.minFreeAmountMicros` is the ONLY source for the number in this
   * message — see the type's docblock. Absent field, absent memo output: this
   * repo does not fall back to a guessed minimum, ever. That is also why the
   * check is skipped for `recurring`: that kind has no free amount to bound,
   * only presets the backend already keeps above the floor.
   */
  const customAmountError = useMemo(() => {
    if (kind !== 'one-off') return null;
    if (!options?.minFreeAmountMicros) return null;
    if (!customAmount) return null;

    /*
      El piso llega del backend y NO se le cree la forma. `BigInt()` lanza un
      SyntaxError crudo ante cualquier cosa que no sea un entero, y esto corre
      en tiempo de render dentro de un memo: un piso mal formado no daría un
      mensaje raro, se llevaría el render completo del modal y el cliente se
      quedaría sin poder recargar.

      Y la forma de este campo no la garantiza nadie de este lado: llega
      dentro de un payload que compone otro servicio (RET-1895).

      Un piso ilegible se trata como AUSENTE, que es el mismo camino que un
      payload sin el campo: sin cota del lado del cliente y el rechazo lo da
      el servidor. La alternativa —bloquear el campo— le quitaría al cliente
      una recarga válida por un dato que él no escribió.

      Y el mismo criterio cubre un piso NUMÉRICO pero indisplayable: con más
      de MAX_READABLE_MIN_FREE_AMOUNT_DIGITS dígitos, `formatWalletAmount`
      lanza ese mismo `RangeError` DENTRO de este memo, al construir el
      mensaje de abajo — el `/^\d+$/` de arriba lo deja pasar porque sí es un
      entero, solo que uno que no cabe. No se envuelve en try/catch porque ese
      `RangeError` es deliberado (evita imprimir una cifra silenciosamente
      incorrecta); se le aplica la misma regla que a la forma: ilegible =
      ausente.
    */
    if (
      !/^\d+$/.test(options.minFreeAmountMicros) ||
      options.minFreeAmountMicros.length > MAX_READABLE_MIN_FREE_AMOUNT_DIGITS
    ) {
      return null;
    }

    const typedMicros = customAmountToMicros(customAmount);
    // Not a clean decimal yet (mid-typing, or empty after sanitising): the
    // submit-time `invalidAmount` guard owns that case, not this one.
    if (typedMicros === null) return null;

    if (BigInt(typedMicros) >= BigInt(options.minFreeAmountMicros)) return null;

    return t('topUp.errors.belowMinimum', {
      // Same formatter the presets use, so the figure in the error reads
      // like the buttons above it — and lands in the right currency.
      min: formatWalletAmount({
        amountMicros: options.minFreeAmountMicros,
        currency: options.currency,
        locale,
        trimZeroCents: true,
      }),
    });
  }, [kind, customAmount, options, locale, t]);

  /**
   * El cobro que ya está en vuelo, listo para comparar Y para nombrar
   * (RET-1925, criterio 5).
   *
   * UNA sola derivación para las dos cosas, y no dos, porque las dos hacen la
   * MISMA pregunta —"¿se puede usar este monto?"— y dos respuestas a esa
   * pregunta se separan. La primera versión las tuvo separadas y el bug fue
   * inmediato: la etiqueta se componía en cada render sin la guarda del
   * comparador, así que un micro ilegible reventaba el modal ENTERO antes de
   * cualquier clic. Una pantalla en blanco en el modal de recarga es mucho peor
   * que no tener candado, y este archivo ya documenta que `formatWalletAmount`
   * lanza `RangeError`.
   *
   * `null` significa "no se puede usar", NUNCA "no coincide": sin monto
   * comparable no hay candado, porque un candado que muerde sin saber contra qué
   * frena una intención legítima. Cubre las tres formas de no saber: no hay
   * espera, la espera no trajo monto, y el monto no es un entero legible.
   *
   * El AGOTADO también cuenta: venció el presupuesto de espera, no el cobro. El
   * dinero salió igual, así que repetirlo es igual de real.
   */
  const chargeInFlight = useMemo((): {
    micros: bigint;
    label: string;
  } | null => {
    // `options` puede ser null aquí: este memo vive ARRIBA del `return null`
    // temprano, porque un hook después de un retorno condicional rompe las
    // reglas de hooks y revienta el árbol en el primer render sin payload.
    if (!options) return null;
    if (settlement.kind === 'idle') return null;
    // Redundante con el `catch` de abajo para toda entrada alcanzable, y se
    // deja escrito a propósito: `BigInt(null)` lanza y `BigInt('')` devuelve 0n,
    // así que las dos formas de "sin monto" terminan sin candado por caminos
    // distintos. La mutación que quita esta línea no mata ninguna prueba —
    // medido, no supuesto— así que lo que sostiene la conducta es el `catch`.
    // Esta línea es la que la hace legible.
    if (!settlement.amountMicros) return null;

    try {
      return {
        micros: BigInt(settlement.amountMicros),
        label: formatWalletAmount({
          amountMicros: settlement.amountMicros,
          currency: options.currency,
          locale,
          trimZeroCents: true,
        }),
      };
    } catch {
      return null;
    }
  }, [settlement, options, locale]);

  if (!options) {
    // Sin opciones hay dos estados: todavía cargando (no se pinta nada, igual
    // que antes) o la primera carga falló (RET-2280: se dice por qué, en vez
    // de dejar el clic en «Recargar» sin respuesta). Va después de todos los
    // hooks, como el `return null` que reemplaza.
    return optionsLoadFailure ? (
      <TopUpOptionsErrorModal
        isOpen={isOpen}
        kind={optionsLoadFailure}
        onClose={onClose}
      />
    ) : null;
  }

  /*
   * RET-2425 (KTD4): opened as recurring or as threshold, the modal stays in
   * its loading state until BOTH reads settle, so the options never change
   * after the first paint. Opened as one-off it does not wait: three of the six
   * mount points are the chat composer with the shortfall already filled, and
   * the GET reads Stripe live. There the threshold option's spot holds a
   * placeholder (`thresholdOptionLoading`).
   *
   * Opened as threshold it also waits for THIS workspace's flags: before they
   * load, "no flag" is the defaults talking, and the request would fall to the
   * recurring top-up for a customer who may well have the threshold one.
   * `initialKind`, not `requestedKind`: a click inside an open modal must
   * never send it back to the loading state.
   */
  const waitsForThreshold =
    (initialKind === 'threshold' && !hasLoadedThresholdTopUpFlag) ||
    (initialKind !== 'one-off' && thresholdPending);

  if (waitsForThreshold) return null;

  /**
   * The amount to charge, in exact micro-units.
   *
   * A selected preset is already exact and travels untouched. A typed amount is
   * converted here, once, from the string the customer wrote — and only when it
   * is a clean decimal, so "12.5.5" or "abc" never reach the charge.
   */
  function amountMicrosToCharge(): string | null {
    if (selectedPresetMicros) return selectedPresetMicros;

    return customAmountToMicros(customAmount);
  }

  /**
   * Por VALOR y no por cadena: `customAmountToMicros` normaliza, pero comparar
   * strings haría que un cambio de formato del back —"25000000" contra
   * "025000000"— leyera dos veces el mismo monto como dos distintos, y el
   * candado dejaría pasar el caso exacto que existe para frenar. Es el mismo
   * criterio con el que `Wallet.settlement.ts` compara saldos.
   */
  function isRepeatOfChargeInFlight(candidateMicros: string): boolean {
    if (!chargeInFlight) return false;

    try {
      return BigInt(candidateMicros) === chargeInFlight.micros;
    } catch {
      return false;
    }
  }

  /*
   * RET-2425: the server's 409 wins over the GET's read (see
   * `serverTypeBlock`). PD-4 from a 409 has no date: the copy without a date
   * says the cancellation is true without inventing the day.
   */
  const effectiveTypeBlocked: tTopUpTypeBlocked | null =
    typeBlocked ??
    (serverTypeBlock ? { ...serverTypeBlock, dateLabel: '' } : null);
  /**
   * RET-2426 (U6, RC-21): the footer line of a one-off over a paused threshold
   * top-up. `thresholdState` already means flag on, role allowed, the GET read
   * and the same currency as the options. A plain call, not a memo: the early
   * returns above sit before it, and the pure function is cheap.
   *
   * Only when this one-off sends no card (`indicatedPaymentMethodId` null):
   * wallet computes `pause.oneOffResumes` for a one-off WITHOUT
   * `paymentMethodId`, and a one-off paid with another card does not end the
   * pause (contract A.3).
   */
  const pausedThreshold =
    kind === 'one-off' &&
    thresholdState &&
    !customAmountError &&
    indicatedPaymentMethodId === null
      ? pausedThresholdOf({
          state: thresholdState,
          balance,
          amountMicros: amountMicrosToCharge(),
          locale,
        })
      : null;
  const effectiveScheduledCancellation =
    scheduledCancellation ?? (serverCancellation ? { dateLabel: '' } : null);
  const cancellationScheduled = isScheduledCancellation || serverCancellation;

  /**
   * RET-2425 (R11): the recurring top-up being configured turns a threshold
   * top-up off. Only without a recurring one in force: with one, it is today's
   * replacement and the threshold top-up cannot be active at the same time.
   */
  const recurringReplacesThreshold =
    thresholdDesign && !activeRecurring && activeThreshold !== null;

  /**
   * RET-2425: the threshold form with a 400 of the PUT painted where it
   * belongs. A limit code goes to the limit's error. An amount code goes to
   * the free amount's error, or, with a preset selected, to the amount rule
   * line marked as an error (`amountInvalid`). Each of the three already keeps
   * Continue off in the component.
   */
  const thresholdView = thresholdForm
    ? (() => {
        const serverAmountError =
          thresholdFieldError?.field === 'amount'
            ? thresholdFieldError.text
            : null;
        const usesCustom = thresholdForm.selectedPresetMicros === null;

        return {
          info: {
            ...thresholdForm.info,
            error:
              thresholdFieldError?.field === 'threshold'
                ? thresholdFieldError.text
                : thresholdForm.info.error,
            amountHint:
              serverAmountError && !usesCustom
                ? serverAmountError
                : thresholdForm.info.amountHint,
            amountInvalid:
              Boolean(serverAmountError && !usesCustom) ||
              thresholdForm.info.amountInvalid,
          },
          customAmountError:
            serverAmountError && usesCustom
              ? serverAmountError
              : thresholdForm.customAmountError,
        };
      })()
    : null;

  /** Formats a threshold amount the way the presets read. */
  function thresholdLabelOf(
    amount: tThresholdAmount,
    currency: tThresholdTopUpState['currency'],
  ): string {
    return formatWalletAmount({
      amountMicros: amount.amountMicros,
      currency,
      locale,
      trimZeroCents: true,
    });
  }

  /**
   * The state wallet holds IS the pair this PUT asked for (the save flow: a 200
   * with another state is a contract error; a 424 whose re-read shows the pair
   * was saved). Compared by value, in micro-units.
   */
  function holdsRequestedValues(
    state: tThresholdTopUpState,
    requested: { thresholdMicros: bigint; amountMicros: bigint },
  ): boolean {
    if (state.status !== 'active' || !state.threshold || !state.amount) {
      return false;
    }

    try {
      return (
        BigInt(state.threshold.amountMicros) === requested.thresholdMicros &&
        BigInt(state.amount.amountMicros) === requested.amountMicros
      );
    } catch {
      return false;
    }
  }

  /**
   * What the success notice says (R18), from what wallet returned: a charge
   * of THIS save, else a charge already in progress, else the limit it charges
   * from. Never from the front's own prediction (KTD8).
   */
  function thresholdOutcomeOf(result: tThresholdSaveResult): tTopUpOutcome {
    const { state, charge } = result;

    if (charge) {
      return {
        kind: 'threshold-charged',
        charge: { status: charge.status },
        amountMicros: charge.amount.amountMicros,
        amountLabel: thresholdLabelOf(charge.amount, state.currency),
      };
    }

    if (state.chargeInProgress) {
      return {
        kind: 'threshold-charge-in-progress',
        amountLabel: thresholdLabelOf(
          state.chargeInProgress.amount,
          state.currency,
        ),
      };
    }

    return {
      kind: 'threshold-configured',
      thresholdLabel: state.threshold
        ? thresholdLabelOf(state.threshold, state.currency)
        : '',
    };
  }

  /**
   * Re-reads the threshold state after a write that did not answer cleanly
   * (KTD7). A failed re-read keeps the state on screen and returns null: the
   * form must not disappear in the middle of a save.
   */
  async function rereadThreshold(): Promise<tThresholdTopUpState | null> {
    try {
      const state = await WalletService.getThresholdTopUp(workspaceId);
      setThresholdLoad({ status: 'ready', state });

      return state;
    } catch {
      return null;
    }
  }

  /**
   * Re-reads the options after a 409 `recurring_active`: the recurring top-up
   * the modal opened without is in them (`activeRecurring`), and the
   * confirmation names its amount. A failure keeps the options on screen.
   */
  async function rereadOptions(): Promise<void> {
    try {
      const data = await WalletService.getTopUpOptions(workspaceId);
      setOptions(data);
    } catch {
      // The confirmation still opens; it names the amount it knows.
    }
  }

  /**
   * Saves the threshold top-up: `PUT …/topup/threshold` (RET-2425, U6). THIS
   * CALL CAN CHARGE A CARD (RC-20). The flow is the diagram of the plan, one
   * branch per answer.
   *
   * Every guard the button already holds is repeated here as a real guard: a
   * guard that lives only in `disabled` is one Enter or one remount away from
   * not guarding.
   */
  async function saveThreshold(replaceRecurring: boolean) {
    if (thresholdSaveInFlightRef.current) return;
    // The role (R19) and the flag: `thresholdDesign` holds both.
    if (!thresholdDesign || kind !== 'threshold') return;
    if (!thresholdForm || !thresholdView || !options?.hasPaymentMethod) return;
    // RC-22: the threshold type cannot be chosen now.
    if (effectiveTypeBlocked?.kind === 'threshold') return;
    // PD-4 / KTD9: a new one is not offered; one in force can be edited.
    if (cancellationScheduled && !activeThreshold) return;

    const { draft, thresholdMicros, amountMicros } = thresholdForm;

    if (
      !thresholdForm.isValid ||
      thresholdView.info.error ||
      thresholdView.info.amountInvalid ||
      thresholdView.customAmountError ||
      thresholdForm.info.unchanged ||
      thresholdMicros === null ||
      amountMicros === null
    ) {
      return;
    }

    // RC-06 / RC-22: replacing the recurring top-up (or the converted credit
    // package, KTD1: the PUT turns it off by itself) asks first.
    if (activeRecurring && !replaceRecurring) {
      setAskingThresholdReplace(true);

      return;
    }

    const requested = { thresholdMicros, amountMicros };
    const request = {
      // Whole units, as text (the validation above already required whole).
      threshold: (thresholdMicros / 1_000_000n).toString(),
      amount: (amountMicros / 1_000_000n).toString(),
    };

    thresholdSaveInFlightRef.current = true;
    setIsSubmitting(true);
    setErrorMessage(null);
    // The values of THIS request stay on screen whatever the re-read brings:
    // without this, an untouched form re-derives from the new state.
    setThresholdDraftEdit(draft);

    const attemptId = readOrCreateThresholdAttemptId(workspaceId);

    try {
      let result: tThresholdSaveResult;

      try {
        result = await WalletService.saveThresholdTopUp(workspaceId, {
          ...request,
          attemptId,
          replaceRecurring,
        });
      } catch (error) {
        setAskingThresholdReplace(false);
        await resolveThresholdFailure(error, requested);

        return;
      }

      // A 200 closes the attempt (KTD6), whatever it carries: the same key
      // with the same values would only replay this answer.
      if (closesThresholdAttempt(null)) clearThresholdAttemptId(workspaceId);
      setAskingThresholdReplace(false);

      const holdsRequested = holdsRequestedValues(result.state, requested);

      if (result.charge !== null) {
        // A 200 with a charge IS a charge, whatever state it holds. Wallet
        // answers exactly this when another write changed the row after it
        // charged (`superseded`), and on a same-key replay of a row turned off
        // later (`sameKeyCharge`, RC-17). The charge is announced and settled
        // (`threshold-charged`: pending → the pending notice); it is not a
        // contract error, and «no se guardó» would deny a real charge.
        onOutcome?.(thresholdOutcomeOf(result));

        if (holdsRequested) {
          if (isMountedRef.current) onClose();

          return;
        }

        // The configuration in force is not the one asked for: the modal
        // stays open and re-reads, so the form says what wallet holds.
        await rereadThreshold();

        return;
      }

      if (!holdsRequested) {
        // A contract error (WalletContractError pattern): wallet said 200,
        // charged nothing, and holds something else. No success notice; the
        // state on screen is re-read so the form says what is true.
        sentry.captureException(
          new WalletContractError(
            'wallet threshold top-up: 200 does not hold the requested values',
          ),
          { extra: { workspaceId, status: result.state.status } },
        );
        await rereadThreshold();
        setErrorMessage(thresholdSaveGenericMessage(t));

        return;
      }

      onOutcome?.(thresholdOutcomeOf(result));
      if (isMountedRef.current) onClose();
    } finally {
      thresholdSaveInFlightRef.current = false;
      setIsSubmitting(false);
    }
  }

  /** Every non-200 of the threshold PUT, per the diagram of the plan. */
  async function resolveThresholdFailure(
    error: unknown,
    requested: { thresholdMicros: bigint; amountMicros: bigint },
  ) {
    // 402 and 400 close the attempt; 409, 403, 424, 5xx and a dropped
    // connection keep it, so the resend goes with the same key (KTD6).
    if (closesThresholdAttempt(error)) clearThresholdAttemptId(workspaceId);

    const failure = thresholdSaveFailure(error, {
      currency: thresholdState?.currency ?? options?.currency ?? 'USD',
      locale,
      t,
    });

    switch (failure.kind) {
      case 'message':
        setErrorMessage(failure.message);

        return;

      case 'field':
        setThresholdFieldError({ field: failure.field, text: failure.text });

        return;

      case 'replace':
        // The modal did not know the type in force. This 409 can come BEFORE
        // a charge (window 1) or AFTER one (window 2 refuses the type change
        // once the charge went through; wallet records only the charge). The
        // confirmed replacement reuses this attempt and this key either way:
        // after a charge the mark carries this key, so the resend replays
        // that charge (`sameKeyCharge`) and never charges again. The re-read
        // then shows `chargeInProgress`, so the confirmation does not say
        // «Hoy cobramos».
        await Promise.all([rereadThreshold(), rereadOptions()]);
        setAskingThresholdReplace(true);

        return;

      case 'typeBlocked':
        await rereadThreshold();
        setServerTypeBlock({ kind: 'threshold', reason: failure.reason });

        return;

      case 'retention':
        await rereadThreshold();
        setServerCancellation(true);

        return;

      case 'unknown': {
        // KTD7: a 424 or a dropped connection does not say whether wallet
        // charged or saved. The state says it, and `chargeInProgress` first.
        const state = await rereadThreshold();

        if (!state) {
          // Nothing says whether wallet charged or saved: the one-off's text
          // for the same unknown class, never the definite «no se guardó».
          // It does not block, and the attempt stays open, so a resend goes
          // with the same key.
          setErrorMessage(chargeErrorMessage(error, t));

          return;
        }

        const isSaved = holdsRequestedValues(state, requested);

        if (isSaved) {
          // Closing it now avoids a replay: with the key alive, a resend of
          // the same values gets a 200 that writes nothing.
          clearThresholdAttemptId(workspaceId);
          onOutcome?.(thresholdOutcomeOf({ state, charge: null }));
          if (isMountedRef.current) onClose();

          return;
        }

        if (state.chargeInProgress) {
          // A charge is in flight and these values are not saved. A resend
          // with THIS key does not save them: wallet routes it to the charge
          // of the key (`sameKeyCharge`) and answers with its unknown outcome,
          // an unmapped error, so a 500 that lands here again. The notice
          // says why, every time; Continue stays live and the attempt open,
          // so no resend can charge twice.
          setErrorMessage({
            text: t('topUp.threshold.inProgress', {
              amount: thresholdLabelOf(
                state.chargeInProgress.amount,
                state.currency,
              ),
            }),
            blocksContinue: false,
            clearsOnNewCard: false,
          });

          return;
        }

        setErrorMessage(thresholdSaveGenericMessage(t));

        return;
      }

      default: {
        const unhandled: never = failure;

        return unhandled;
      }
    }
  }

  /**
   * El alta O el reemplazo de la recarga automática (RET-1692 / RET-1935).
   *
   * Cuál de las dos operaciones se llama lo decide `activeRecurring` —el MISMO
   * memo que abrió esta confirmación— y no un parámetro ni un estado aparte:
   * dos fuentes decidiendo lo mismo es exactamente cómo se llega a llamar la
   * equivocada.
   *
   *   - CON una activa: `WalletService.replaceRecurringTopUp`. Antes de
   *     RET-1935 este camino llamaba a `enableRecurringTopUp`, con el
   *     comentario "repetirlo reemplaza, no cobra dos veces" — que es FALSO:
   *     con una recarga ya activa, el alta SIEMPRE responde 409
   *     (`ErrRecurringTopUpAlreadyActive`, GUARD 2 de wallet-service). El
   *     reemplazo era, por esa puerta, imposible.
   *   - SIN una activa: `WalletService.enableRecurringTopUp`, como siempre.
   *
   * NUNCA se encadena una baja seguida de un alta para simular el reemplazo:
   * un fallo entre las dos llamadas dejaría al cliente sin recarga automática
   * en absoluto, y wallet-service ya resuelve el intercambio como una sola
   * escritura atómica detrás del PUT.
   *
   * El monto viaja en DECIMAL y tomado TAL CUAL del preset, no recompuesto
   * desde los micros: wallet-service exige que coincida exacto con uno de sus
   * presets, y un `50.0` reconstruido no casa con `50.000000`.
   *
   * Ninguna de las dos lleva llave de idempotencia porque ninguna es un cobro:
   * configuran un line item que se cobrará en la factura del próximo ciclo.
   */
  async function confirmRecurring() {
    // Re-verificado AQUÍ, no solo en `submit()` (docs/solutions/logic-errors/
    // websocket-race-overwrites-guard-state-during-await.md): este guardia
    // también se alcanza desde `ReplaceRecurringModal.onConfirm`, un diálogo
    // que queda abierto esperando al cliente mientras el contexto de
    // suscripción sigue vivo. Si la baja se agenda MIENTRAS ese diálogo está
    // abierto, `isScheduledCancellation` ya lee el valor nuevo en este punto
    // (se recalcula cada render, no se capturó antes del `await`), así que
    // repetir la lectura en el punto de ejecución basta — no hace falta
    // capturarla en un const aparte.
    // RET-2425: also when the cancellation came from wallet's 409.
    if (isScheduledCancellation || serverCancellation) {
      setAskingToReplace(false);

      return;
    }

    const preset = options?.recurring?.presets.find(
      (candidate) => candidate.amountMicros === selectedPresetMicros,
    );

    if (!preset) {
      // Validación LOCAL, no un intento de cobro: no hay `error` de red que
      // clasificar, así que la clase se pone a mano y siempre bloquea — no
      // hay nada que reintentar hasta que el cliente elija un monto real
      // (RET-1933).
      setErrorMessage({
        text: t('topUp.errors.invalidAmount'),
        blocksContinue: true,
        // Habla del monto, no de la tarjeta (RET-2348).
        clearsOnNewCard: false,
      });

      return;
    }

    // Se lee UNA vez, al entrar: es la misma condición que decidió abrir esta
    // confirmación (o que la saltó por completo, en el alta sin activa), y
    // capturarla evita que un cambio de estado a mitad de un `await` haga que
    // la llamada y el mensaje de error de más abajo decidan sobre condiciones
    // distintas.
    const isReplace = Boolean(activeRecurring);
    // RET-2425 (R11): read once, like `isReplace`, for the same reason.
    const replacesThreshold = recurringReplacesThreshold;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      if (isReplace) {
        await WalletService.replaceRecurringTopUp(workspaceId, preset.amount);
      } else if (replacesThreshold) {
        // The confirmation was accepted: the threshold top-up turns off in
        // the same operation (RC-22).
        await WalletService.enableRecurringTopUp(workspaceId, preset.amount, {
          replaceThreshold: true,
        });
      } else {
        await WalletService.enableRecurringTopUp(workspaceId, preset.amount);
      }
      setAskingToReplace(false);
      // El alta y el reemplazo SÍ son un desenlace, y hasta RET-1928 salía por
      // la misma puerta que cerrar la ventana: el renglón quedaba
      // agregado/cambiado en Stripe y la pantalla no decía nada. Viaja con los
      // labels que el resumen ya calculó — el mismo valor, no un segundo
      // formateo que podría discrepar de lo que el cliente acaba de leer.
      //
      // No arranca ninguna espera de acreditación: se cobra en la siguiente
      // renovación, así que no viene crédito alguno. Eso lo decide la tabla de
      // `Wallet.outcome.ts`, no este archivo.
      onOutcome?.({
        kind: replacesThreshold
          ? 'recurring-replaced-threshold'
          : 'recurring-enabled',
        startDateLabel: recurring?.startDateLabel ?? '',
        // El ADVERBIO, no `cadenceLabel`. La frase `topUp.start.explain` dice
        // "se repite {{cadence}}", así que interpolar el sustantivo la deja en
        // "se repite mensual". Son dos campos distintos en el mismo memo y se
        // parecen lo bastante como para tomar el equivocado sin notarlo.
        cadenceLabel: recurring?.cadenceAdverbLabel ?? '',
      });
      onClose();
    } catch (error) {
      // Se cierra la confirmación ANTES de poner el error. El error se pinta en
      // el modal de recarga, que está por DEBAJO: dejarla abierta lo tapa, y el
      // cliente ve una confirmación que no hizo nada y no dice por qué. En una
      // pantalla que cancela un cobro configurado, eso invita a volver a pulsar.
      setAskingToReplace(false);

      // RET-2425 (RC-22): the 409s of the POST that concern the threshold
      // top-up are resolved with its state, as the PUT's are. Only with the
      // new design: without it this modal cannot show a threshold top-up, and
      // the 409 keeps today's text (the Risks table of the plan).
      if (thresholdDesign) {
        const conflict = recurringThresholdConflict(error);

        if (conflict === 'threshold_active') {
          const state = await rereadThreshold();

          if (state && thresholdInForce(state)) {
            setAskingToReplace(true);

            return;
          }
        } else if (conflict === 'cancellation_scheduled') {
          await rereadThreshold();
          setServerCancellation(true);

          return;
        } else if (conflict) {
          await rereadThreshold();
          setServerTypeBlock({ kind: 'recurring', reason: conflict });

          return;
        }
      }

      // C6 (RET-1935): un rechazo de la ruta recurrente —huérfano, congelado,
      // ya activa, sin activa— es PERMANENTE, así que la copy no puede
      // prometer un reintento Y el botón se queda apagado. La clase viaja
      // pegada al texto, que es el mecanismo de RET-1933.
      //
      // `recurringErrorMessage` traduce la razón de máquina que manda el back
      // y delega en `chargeErrorMessage` todo lo demás — incluido, y esto es
      // lo que importa no romper, lo que NO debe apagar Continuar: el 402 de
      // tarjeta, el 424, los 5xx y la red caída.
      setErrorMessage(recurringErrorMessage(error, isReplace, t));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function submit() {
    // RET-2348: el clic en el botón principal es la respuesta a «tu recarga
    // todavía no se cobra», así que la línea se apaga AQUÍ, antes de cualquier
    // salida temprana. Si el intento falla, lo que se lee es su error; la
    // línea de una tarjeta guardada antes de ese intento ya no describe nada.
    setCardSaved(false);

    // RET-2425: a threshold request whose option is gone fails closed. The
    // flag or the role (R19, RET-2115: only Owner and Admin top up) can turn
    // `thresholdDesign` off with the modal open; `kind` then falls from
    // 'threshold' to 'recurring' or 'one-off', and the click meant to save the
    // threshold top-up would post that other form instead. Two cases trip it:
    // - the option WAS shown in this instance (`thresholdShownRef`): the
    //   request is stale. An explicit pick of another kind (`onKindChange`)
    //   replaces `requestedKind`, so that pick is honoured.
    // - the role cannot top up at all, shown or not (today's R19 guard).
    // A request that was never servable (flag off from the start, wallet 404,
    // the GET failed) is not stale: the fallback form is what the customer saw
    // from the first paint, and saving it keeps working (U5).
    // Scoped to the threshold request on purpose: the one-off and recurring
    // paths keep today's behaviour (the entry points hide them from other
    // roles and the BFF answers 403).
    if (
      requestedKind === 'threshold' &&
      kind !== 'threshold' &&
      (thresholdShownRef.current || !canTopUp)
    ) {
      setErrorMessage(thresholdSaveGenericMessage(t));

      return;
    }

    // RET-2425: the threshold top-up has its own write, and it ALWAYS stops
    // here. Falling through would reach the one-off path below and CHARGE the
    // threshold form's amount as a one-off top-up.
    if (kind === 'threshold') {
      await saveThreshold(false);

      return;
    }

    // El alta recurrente teniendo una activa exige confirmación explícita antes
    // de tocar el back (D-6): reemplazar cancela un cobro que el cliente ya
    // había configurado, y eso no puede ocurrir por un clic de inercia.
    if (kind === 'recurring') {
      // PD-4: la recarga automática no se ofrece con la baja agendada — no
      // habría próxima renovación que la cobre. Continuar ya llega
      // deshabilitado (TopUpModal.component.tsx) y el momento de retención es
      // la única salida visible, pero el bloqueo tiene que ser REAL: un
      // guardia que solo viviera en el atributo `disabled` es un clic de
      // distancia (Enter, un remount con otro estado) de dejar de bloquear.
      if (isScheduledCancellation) return;
      // RET-2425: the same PD-4 guard when the answer came from wallet's 409.
      if (serverCancellation) return;
      // RC-22: the recurring type cannot be chosen now.
      if (effectiveTypeBlocked?.kind === 'recurring') return;

      // RET-2259: un paquete de créditos convertido no se reemplaza desde aquí.
      // Continuar ya llega deshabilitado y el aviso dice que primero hay que
      // apagarlo desde el switch; el guardia se repite aquí por la misma razón
      // que el de arriba. El PUT de reemplazo no conoce el paquete.
      if (activeRecurring?.source === 'credit_package') return;

      // Se pregunta por `activeRecurring` (el memo), NO por el crudo
      // `options.activeRecurring.active`. Eran dos condiciones decidiendo lo
      // mismo y no coincidían: el memo exige además que venga el MONTO, así que
      // un `active: true` sin monto abría la confirmación con la frase
      // "reemplaza la de ___" vacía. Una sola fuente.
      // RET-2425 (R11): replacing a threshold top-up asks too — it turns off
      // at once, and the confirmation says the gap until the first charge.
      if ((activeRecurring || recurringReplacesThreshold) && !askingToReplace) {
        setAskingToReplace(true);

        return;
      }

      await confirmRecurring();

      return;
    }

    // Belt-and-suspenders: Continue is already disabled while this holds
    // (TopUpModal.component.tsx), so a real click can't reach here — but a
    // guard that only lives in the disabled attribute is one CSS class away
    // from being the only thing standing between a stale total and a charge.
    if (customAmountError) return;

    const amountMicros = amountMicrosToCharge();

    if (!amountMicros) {
      // Validación LOCAL, no un intento de cobro: no hay `error` de red que
      // clasificar, así que la clase se pone a mano y siempre bloquea (mismo
      // criterio que el guardia gemelo de `enableRecurring`, RET-1933).
      setErrorMessage({
        text: t('topUp.errors.invalidAmount'),
        blocksContinue: true,
        // Habla del monto, no de la tarjeta (RET-2348).
        clearsOnNewCard: false,
      });

      return;
    }

    // EL CANDADO (RET-1925, criterio 5). Aquí y no en el `disabled` del botón,
    // por la misma razón que la guarda de `customAmountError` unas líneas
    // arriba: un guardia que sólo vive en un atributo está a un Enter o a un
    // remount de dejar de guardar. Y aquí abajo, después de resolver el monto,
    // porque el candado es POR MONTO y no hay nada que comparar antes.
    //
    // Fricción, no bloqueo: con la bandera ya en true el `onConfirm` del
    // diálogo vuelve a pasar por esta misma línea y cae de largo.
    if (!repeatConfirm && isRepeatOfChargeInFlight(amountMicros)) {
      setRepeatConfirm({ label: chargeInFlight?.label ?? '' });

      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      // RC-16: la tarjeta va fijada al intento, no a esta petición. Un intento
      // abierto manda la tarjeta con la que salió la primera vez, aunque ahora
      // se indique otra (ver `readOrCreateOneOffAttempt`).
      const attempt = readOrCreateOneOffAttempt({
        workspaceId,
        paymentMethodId: indicatedPaymentMethodId,
      });
      const outcome = await WalletService.chargeTopUp(workspaceId, {
        amountMicros,
        attemptId: attempt.id,
        paymentMethodId: attempt.paymentMethodId ?? undefined,
      });
      // Deliberately does NOT repaint the balance: a 200 means charged, not
      // credited — the ledger row lands via webhook. The repaint arrives by
      // push: wallet-service signals wallet.balance.updated after crediting
      // and WalletProvider refetches (ADR-013, RET-1870).
      //
      // What changed with RET-1870 is the FALLBACK for a dropped socket. It
      // used to be the single refetch each mount site fired on close, which
      // raced the webhook and usually read the balance from before the credit.
      // The outcome below hands the charge to the wallet provider instead, and
      // it runs a bounded settlement wait. Why that wait exists, and what each
      // charge status starts, is stated on `settleBalanceAfterCharge` in
      // `Wallet.provider.tsx`.
      //
      // Desenlace DEFINITIVO: cierra la intención (ver la nota, abajo).
      clearAttemptId(workspaceId);
      // `amountMicros` es la MISMA variable que se mandó al endpoint (RET-1925).
      //
      // Y hay que ser preciso sobre qué lo garantiza, porque la razón obvia no
      // es la verdadera: `submit` captura el estado de SU render, así que volver
      // a llamar a `amountMicrosToCharge()` aquí devolvería el mismo valor
      // aunque el cliente haya tocado otro preset durante el POST —los presets
      // no se deshabilitan mientras el cobro vuela, así que puede hacerlo—.
      // Verificado por mutación: releer al emitir no rompe ninguna prueba.
      //
      // O sea que la invariante la sostiene el cierre de JavaScript, no esta
      // línea. Se escribe así de todas formas porque este archivo ya usa refs
      // para leer valores frescos a través de un await, y el día que alguien
      // haga eso con la selección, la captura explícita es la diferencia entre
      // cobrar 50 y dejar el candado vigilando 200. La prueba que fija esa
      // conducta está en el bloque RET-1925 del archivo de pruebas.
      // REDUNDANTE hoy, y medido: `onClose()` hace que `useTopUpModal` desmonte
      // el modal entero, así que este estado muere de todas formas y quitar esta
      // línea no mata ninguna prueba. Se escribe porque el hermano lo hace en su
      // camino de éxito y porque la redundancia sólo se sostiene mientras el
      // montaje sea condicional: un llamador que mantenga el modal montado y
      // sólo alterne `isOpen` —hay arneses de prueba que ya lo hacen— dejaría el
      // candado desarmado tras el primer cobro. El reset del `catch`, en
      // cambio, NO es redundante: ahí el modal se queda abierto.
      setRepeatConfirm(null);
      // RET-2426 (U6): lo que la respuesta dijo de la recarga por saldo bajo, y
      // contra qué confirmará la relectura de después (KTD9). El workspace es
      // el del pago, capturado con este render: si la pantalla ya cambió
      // cuando llegue la relectura, no se aplica. Solo con el diseño nuevo
      // (bandera y rol): sin él, el desenlace es el de siempre y no sale
      // ninguna lectura nueva.
      const resumption =
        thresholdDesign && outcome.thresholdTopUp
          ? {
              thresholdTopUp: outcome.thresholdTopUp,
              resumption: {
                workspaceId,
                chargesBefore:
                  thresholdLoad.status === 'ready'
                    ? thresholdLoad.state.today.count
                    : null,
              },
            }
          : {};
      onOutcome?.({
        kind: 'charged',
        charge: outcome,
        amountMicros,
        ...resumption,
      });
      onClose();
    } catch (error) {
      /*
      LA REGLA, y por qué es simétrica (JC, spike contra Stripe test mode):

      Stripe cachea la respuesta bajo la llave durante 24h, y cachea TAMBIÉN el
      402. Verificado: misma llave tras un rechazo devuelve `Idempotent-Replayed:
      true` con el MISMO PaymentIntent, sin volver a tocar la tarjeta.

      Conservar el attemptId tras un rechazo dejaba al cliente atrapado: cada
      reintento le repetía el rechazo cacheado durante un día entero, sin llegar
      nunca al banco — y el `retryableWithSameCard: true` que contesta el
      endpoint dejaba de significar nada.

      Así que la llave se conserva solo cuando el desenlace es DESCONOCIDO (red
      caída, timeout, 5xx), que es la única ambigüedad que la estabilidad
      protege: "¿se cobró o no?". Ante una respuesta definitiva —200 o 402— no
      hay ambigüedad y la llave rota.
      */
      if (error instanceof WalletDeclinedError) {
        clearAttemptId(workspaceId);
      }

      // ANTES del mensaje de error, y por la misma razón que el hermano: el
      // error se pinta en el modal de recarga, que está por DEBAJO de esta
      // confirmación. Dejarla abierta lo tapa, el cliente ve un botón que no
      // hizo nada, y vuelve a pulsar — sobre un camino que ya no pide
      // confirmación y que, tras un 402, acuña llave nueva.
      setRepeatConfirm(null);
      setErrorMessage(chargeErrorMessage(error, t));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <TopUpModal
        isOpen={isOpen}
        kind={kind}
        // RET-2425: every threshold prop is inert without `thresholdDesign`, and
        // without it nothing here differs from today's modal (KTD2).
        thresholdDesign={thresholdDesign}
        canChooseThreshold={canChooseThreshold}
        thresholdOptionLoading={thresholdPending}
        threshold={
          thresholdForm && thresholdView
            ? {
                ...thresholdView.info,
                onChange: (value) => {
                  setThresholdDraftEdit({
                    ...thresholdForm.draft,
                    threshold: value,
                  });
                  setErrorMessage(null);
                  setThresholdFieldError(null);
                  setCardSaved(false);
                },
              }
            : null
        }
        activeThreshold={activeThreshold}
        pausedThreshold={pausedThreshold}
        typeBlocked={effectiveTypeBlocked}
        /*
        La misma condición que ya decidió `kind`, arriba, y por eso se pasa la
        constante en vez de recomponerla aquí: eran dos expresiones resolviendo
        lo mismo y podían separarse — la pestaña dejaba de ofrecerse mientras el
        tipo seguía siendo 'recurring'.

        Se cierra en la pestaña y no en el render del modal, que fue el intento
        anterior: gatear el render hacía desaparecer la UI en cualquier árbol sin
        el provider resuelto. La recarga ÚNICA sigue disponible —PD-4 la
        permite— y lo único que se pierde mientras no se sabe es el aviso, que es
        informativo. Lo que no se pierde es el dinero: nadie configura un cobro
        recurrente que no se va a cobrar.
      */
        canChooseRecurring={canChooseRecurring}
        presets={presets}
        // RET-2425: the threshold form keeps its own amount, so switching to the
        // one-off and back does not mix the two (each has its own minimum).
        selectedPresetMicros={
          thresholdForm
            ? thresholdForm.selectedPresetMicros
            : selectedPresetMicros
        }
        customAmount={thresholdForm ? thresholdForm.customAmount : customAmount}
        totalLabel={thresholdForm ? thresholdForm.totalLabel : totalLabel}
        /*
        RET-2348: la cifra que el botón NOMBRA, «Recargar $50 USD». Es el mismo
        `totalLabel` del pie —un solo formateo, así que el botón no puede decir
        otra cifra que el total—, pero sólo cuando ESE monto es el que este clic
        cobraría: `amountMicrosToCharge()` es la misma función que `submit()`
        consulta, y `customAmountError` la misma guarda que lo frena. Con «0»
        escrito o un monto bajo el mínimo el total se sigue pintando y el botón
        dice «Recargar» a secas.

        Sólo la única lleva cifra: la automática no cobra con este clic.
      */
        chargeAmountLabel={
          thresholdForm
            ? // RET-2425: only a pair this click could save. With the amount
              // rule broken or a limit error there is nothing to name, and the
              // footer's «se cobra» line goes with it (KTD8).
              thresholdForm.chargeAmountLabel
            : kind === 'one-off' && !customAmountError && amountMicrosToCharge()
              ? totalLabel
              : ''
        }
        recurring={recurring}
        activeRecurring={activeRecurring}
        cardSaved={cardSaved}
        walletCannotSend={walletCannotSend}
        paymentSection={<TopUpPaymentSection />}
        canContinue={
          options.hasPaymentMethod &&
          // La automática solo se da de alta con un preset elegido: sin él, el
          // alta responde "escribe un monto válido" sobre una pantalla donde no
          // hay nada que escribir. Mejor un botón apagado con la escalera a la
          // vista que un botón vivo que culpa al cliente.
          (kind !== 'recurring' || Boolean(selectedPresetMicros)) &&
          // RET-2425: the threshold top-up saves a valid pair only (R3).
          (kind !== 'threshold' || Boolean(thresholdForm?.isValid))
        }
        isSubmitting={isSubmitting}
        // El error de una acción del cliente gana sobre el aviso de recarga
        // fallida (RET-2280): es lo más reciente que hizo, y el aviso vuelve a
        // verse en cuanto ese error se limpia.
        errorMessage={
          errorMessage?.text ??
          (optionsRefreshFailure
            ? t(`topUp.optionsError.${optionsRefreshFailure}`)
            : null)
        }
        // La clase viaja PEGADA al mensaje en el estado — ver `errorMessage`,
        // arriba — y aquí sólo se desempaqueta para las dos props que el
        // presentacional espera (RET-1933). `?? true` no se alcanza nunca en la
        // práctica (con `errorMessage` en `null` el componente ignora esta prop
        // por completo, `Boolean(errorMessage)` ya es `false`), pero es el
        // mismo default fail-cerrado que el propio componente declara.
        errorBlocksContinue={
          errorMessage
            ? errorMessage.blocksContinue
            : optionsRefreshFailure !== 'transient'
        }
        customAmountError={
          thresholdView ? thresholdView.customAmountError : customAmountError
        }
        scheduledCancellation={effectiveScheduledCancellation}
        onKeepPlan={reactivatePlan}
        onClose={onClose}
        onKindChange={(next) => {
          // RC-22: the blocked type ignores the pick. The component already
          // keeps its radio `aria-disabled`; this is the same guard where the
          // state changes.
          if (effectiveTypeBlocked?.kind === next) return;

          setRequestedKind(next);
          setErrorMessage(null);
          setThresholdFieldError(null);
          // RET-2348: «tu recarga todavía no se cobra» hablaba de la recarga que
          // estaba armada. Cambiar de pestaña arma otra, así que la línea se va
          // — igual que en los dos manejadores de monto, abajo.
          setCardSaved(false);

          /*
          CADA TIPO TRAE SU PROPIA SUGERENCIA, y el cambio de pestaña tiene que
          soltar la del otro.

          Solo había rama para 'recurring'. Al volver a 'única' el preset elegido
          en la escalera recurrente se conservaba, y en un NO-mensual las dos
          escaleras no se cruzan (RC-03, ×periodicidad): el monto no existía en
          la lista pintada, así que ningún botón quedaba marcado y el total del
          pie se iba a cadena VACÍA — con Continuar vivo, porque para la única
          no exige preset. `amountMicrosToCharge()` seguía devolviendo el preset
          arrastrado y se cobraba un monto que el cliente no eligió y que la
          pantalla no mostraba en ninguna parte.

          El sugerido de vuelta es el MISMO que se elige al abrir el modal
          (`data.defaultPresetMicros`, D-1): el estado tras cambiar de ida y
          vuelta es el de recién abierto, no un tercer estado inventado.

          Y CUANDO EL MODAL ABRIÓ PARA CUBRIR UN FALTANTE, «recién abierto» es
          el faltante prellenado, no el preset sugerido (RET-2348). Esta rama no
          lo sabía: quien llegaba desde la barra sin saldo del compositor, con
          el monto que cubre su envío ya puesto, se asomaba a la automática y
          al volver encontraba el preset sugerido en su lugar. Es además la
          salida que ofrece el aviso de la pestaña automática («Hacer una
          recarga única»), que entra por ESTA misma función: el botón y la
          tarjeta de arriba hacen el mismo cambio y dejan el mismo estado.

          `resolveShortfallPrefill` devuelve '' cuando no hay faltante o no es
          comparable, y entonces todo queda como antes: campo vacío y preset
          sugerido.
        */
          if (next !== 'recurring') {
            const prefill = resolveShortfallPrefill(initialShortfall, options);

            // Exactamente una de las dos es la fuente del total: con prellenado
            // manda el monto libre y no queda preset marcado, igual que al abrir.
            setCustomAmount(prefill);
            setPickedPresetMicros(
              prefill ? null : options.defaultPresetMicros || null,
            );

            return;
          }

          // A free amount is one-off only, so going to recurring drops it.
          setCustomAmount('');

          /*
            El monto sugerido de la escalera recurrente lo decide el BACKEND, y
            llega en el payload como VALOR (bff-web#914). Este repo no lo
            deduce.

            Antes aquí había una heurística: conservar el default de la recarga
            ÚNICA si ese monto existía también en la escalera de la automática.
            Era andamiaje por un campo que no existía, y estaba mal justo para
            los clientes que el arreglo buscaba — en un trimestral las dos
            listas no se cruzan, así que tiraba la sugerencia que el backend sí
            tenía. Coincidía con el backend solo en el caso mensual, y por
            aritmética: el día que las escaleras cambien y lleguen a cruzarse en
            otro punto, preseleccionaría un monto que el backend no eligió.

            Ausente = sin sugerencia, que es una respuesta completa del contrato
            ("que elija el cliente", igual que un índice fuera de rango) y es
            también el estado durante el rollout. El cliente ve la escalera y
            Continuar apagado hasta que elija: nunca un monto inventado.
        */
          setPickedPresetMicros(options.recurring?.defaultPresetMicros || null);
        }}
        onSelectPreset={(amountMicros) => {
          if (thresholdForm) {
            setThresholdDraftEdit({
              ...thresholdForm.draft,
              presetMicros: amountMicros,
              custom: '',
            });
            setErrorMessage(null);
            setThresholdFieldError(null);
            setCardSaved(false);

            return;
          }

          setPickedPresetMicros(amountMicros);
          setCustomAmount('');
          setErrorMessage(null);
          setCardSaved(false);
        }}
        onCustomAmountChange={(value) => {
          if (thresholdForm) {
            setThresholdDraftEdit({
              ...thresholdForm.draft,
              presetMicros: null,
              custom: value,
            });
            setErrorMessage(null);
            setThresholdFieldError(null);
            setCardSaved(false);

            return;
          }

          setCustomAmount(value);
          // Typing a free amount deselects the preset: exactly one of the two
          // is ever the source of the total.
          setPickedPresetMicros(null);
          setErrorMessage(null);
          setCardSaved(false);
        }}
        onContinue={() => {
          void submit();
        }}
      />

      <RepeatTopUpModal
        isOpen={repeatConfirm !== null}
        isBusy={isSubmitting}
        chargedAmountLabel={repeatConfirm?.label ?? ''}
        onCancel={() => setRepeatConfirm(null)}
        onConfirm={() => {
          // Vuelve a `submit`, no a un camino propio: la llave de idempotencia,
          // su descarte y el manejo del rechazo viven ahí y no se duplican.
          // La bandera ya está en true, así que la guarda deja pasar.
          void submit();
        }}
      />

      {/* The recurring POST: today's replacement, or (RET-2425, R11) the
          recurring top-up replacing a threshold one, which turns off at once
          and leaves a gap until the first charge. */}
      <ReplaceRecurringModal
        isOpen={askingToReplace}
        isBusy={isSubmitting}
        variant={
          recurringReplacesThreshold ? 'recurringReplacesThreshold' : 'recurring'
        }
        currentAmountLabel={
          (recurringReplacesThreshold
            ? activeThreshold?.amountLabel
            : activeRecurring?.amountLabel) ?? ''
        }
        nextChargeLabel={
          (recurringReplacesThreshold
            ? recurring?.startDateLabel
            : activeRecurring?.startDateLabel) ?? ''
        }
        onCancel={() => setAskingToReplace(false)}
        onConfirm={() => {
          void confirmRecurring();
        }}
      />

      {/* RET-2425 (R10): the threshold PUT replacing the recurring top-up or
          the converted credit package. «Hoy cobramos» only when this save
          charges (RC-20): never with a charge in progress (KTD7, KTD8). */}
      <ReplaceRecurringModal
        isOpen={askingThresholdReplace}
        isBusy={isSubmitting}
        variant="thresholdReplacesRecurring"
        currentAmountLabel={activeRecurring?.amountLabel ?? ''}
        nextChargeLabel=""
        chargeNowLabel={
          thresholdForm &&
          thresholdForm.info.belowBalanceLabel &&
          !thresholdForm.info.inProgressLabel &&
          thresholdForm.chargeAmountLabel
            ? thresholdForm.chargeAmountLabel
            : null
        }
        onCancel={() => setAskingThresholdReplace(false)}
        onConfirm={() => {
          void saveThreshold(true);
        }}
      />
    </>
  );
}

/**
 * Bridges Subscription.context's `endDate` (a `Date`) into the RFC3339
 * string `formatWalletDate` expects (Wallet.money.ts).
 *
 * `subscription.endDate` is typed as a bare `Date`, but this is the first
 * consumer that reads it OUTSIDE SubscriptionOverview's own `moment`
 * formatting, and the real world is messier than the type: absent, `null`,
 * or an already-invalid `Date` all happen (partial webhook data, a dirty
 * fixture). `Date.prototype.toISOString` THROWS on an invalid Date instead of
 * returning something formattable, so validity is checked BEFORE the call —
 * never after, and never behind a try/catch that would swallow a real bug
 * alongside a bad date.
 *
 * Undefined in, undefined out: `formatWalletDate` already renders an absent
 * date as an empty string rather than "Invalid Date" or 1970 (see its own
 * docblock), so this bridge does not duplicate that decision — it only
 * converts the shape Subscription.context hands out into the one
 * formatWalletDate accepts.
 *
 * TWO producers, TWO extra guards this bridge owns (both found reviewing this
 * exact change against `Subscription.hook.ts:324`'s `new Date(summaryData.endDate)`):
 *
 * 1. EPOCH IS NOT A VALID DATE HERE. `new Date(null)` — what a JSON `null` in
 *    the wire payload becomes once cast through the (lying) `endDate: string`
 *    type — is NOT `Number.isNaN`: it silently resolves to `1970-01-01T00:00Z`,
 *    a perfectly valid Date. `!endDate` also misses it, `null` already failed
 *    that check by construction and a `Date` object is never itself falsy. No
 *    real cancellation date lands on the Unix epoch, so it is treated the same
 *    as NaN: absent, not "1 de enero de 1970".
 *
 * 2. RE-ANCHORED TO THE VIEWER'S OWN CALENDAR DAY, not passed through
 *    verbatim. `summaryData.endDate` arrives from bff-web as a ZONELESS civil
 *    datetime ("2026-09-03 00:00" — `datetime=2006-01-02 15:04`), and
 *    `new Date(...)` in `Subscription.hook.ts` reads that string as LOCAL
 *    midnight — in whatever timezone the browser that ran it happens to sit
 *    in. `formatWalletDate` renders in UTC ON PURPOSE (its own docblock): it
 *    exists for genuine UTC instants (Stripe's billing-day timestamps), and
 *    feeding it a `.toISOString()` of a LOCAL midnight re-interprets that
 *    civil day as a UTC one, shifting it a day backward for every timezone
 *    east of Greenwich (verified: TZ=Europe/Madrid and TZ=Asia/Tokyo both
 *    paint the day BEFORE the one Subscription.hook.ts's own `moment(...)`
 *    calls already show correctly elsewhere in this app, e.g.
 *    SubscriptionOverview.component.tsx). Reading the LOCAL y/m/d back off the Date
 *    (the same components the string was built from, in the SAME browser)
 *    recovers the intended civil day with no timezone dependency at all, and
 *    re-anchoring it at UTC noon keeps that day intact through
 *    `formatWalletDate`'s UTC render for any viewer offset from UTC-12 to
 *    UTC+12. This does not duplicate `formatWalletDate`'s formatting — it
 *    only chooses WHICH instant to hand it, same as the noon-UTC convention
 *    this module's own tests already use for the same reason.
 */
function subscriptionEndDateToRfc3339(
  endDate: Date | null | undefined,
): string | undefined {
  if (!endDate) return undefined;

  const time = endDate.getTime();

  if (Number.isNaN(time) || time === 0) return undefined;

  const year = endDate.getFullYear();
  const month = endDate.getMonth();
  const day = endDate.getDate();

  return new Date(Date.UTC(year, month, day, 12, 0, 0)).toISOString();
}

/**
 * El monto libre con el que se prellena la recarga única para cubrir un
 * faltante (RET-1806), o '' cuando no hay nada que prellenar.
 *
 * UNA función y no dos copias, porque desde RET-2348 esta regla se aplica en
 * dos momentos —al abrir el modal y al VOLVER a la recarga única desde la
 * automática— y la guarda de abajo es de las que se pierden al copiar.
 *
 * LAS DOS CANTIDADES TIENEN QUE SER COMPARABLES, o no se prellena.
 *
 * `computeTopUpPrefillAmount` hace `max(techo(faltante), piso)`, y el piso
 * llega en la moneda de ESTE payload mientras el faltante llega en la de la
 * cotizacion. Distintas, ese max no da un monto: da un numero. Prellenar el
 * campo con el se lo ofrece al cliente como el importe que va a cobrarse.
 *
 * Hoy coinciden siempre porque wallet-service deriva las dos de la misma
 * suscripcion, pero es una garantia AJENA. Sin comparacion valida se deja el
 * campo vacio con la escalera a la vista, que es una respuesta completa —
 * mismo criterio que "ausente = sin sugerencia".
 */
function resolveShortfallPrefill(
  shortfall: tWalletShortfall | null,
  options: tTopUpOptionsPayload,
): string {
  if (!shortfall) return '';
  if (shortfall.currency !== options.currency) return '';

  return computeTopUpPrefillAmount(
    shortfall.amountMicros,
    options.minFreeAmountMicros,
  );
}

/**
 * La tarjeta que la recarga única indica al cobrar (RET-2428, RC-16), o
 * `null` para que el backend cobre la predeterminada.
 *
 * Indica la tarjeta guardada sin quedar como predeterminada sólo si sigue en
 * la lista, no es la predeterminada del cliente y es la que el modal muestra
 * (o el modal no muestra ninguna). Así la tarjeta que se ve es la que se
 * cobra. Una tarjeta guardada después COMO predeterminada pasa a ser la que
 * se muestra, y esta deja de indicarse.
 */
function resolveIndicatedPaymentMethodId(args: {
  savedWithoutDefaultId: string | null;
  shownId: string | undefined;
  customerDefaultId: string | undefined;
  paymentMethods: tPaymentMethod[];
}): string | null {
  const { savedWithoutDefaultId: id, shownId } = args;

  if (!id) return null;
  if (!args.paymentMethods.some((method) => method.paymentMethodId === id)) {
    return null;
  }
  if (id === args.customerDefaultId) return null;
  if (!shownId) return id;

  return shownId === id ? id : null;
}

/*
El clasificador del error de cobro —qué texto sale, y desde RET-1933 si ESE
texto debe apagar Continuar— vive en `TopUpModal.chargeError.ts`, no aquí:
es una decisión sin React ni red, mismo reparto que `Wallet.outcome.ts` y
`Wallet.settlement.ts`. `isAmountRejection` se mudó con él, y desde RET-1935
también `recurringErrorMessage`: la ruta recurrente clasifica por la misma
regla y con el mismo tipo de salida, así que partirla en dos archivos habría
dejado dos criterios de "esto apaga el botón" en lugares distintos.
*/
